import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  ButtonGroup,
  Button,
  Tab,
  Nav,
  Badge,
  Alert,
  Modal,
  Form,
  Spinner,
  Stack,
  Table
} from 'react-bootstrap';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell
} from 'recharts';
import moment from 'moment';
import { fires } from '../firebase';
import { FaUsers, FaShoppingCart, FaChartLine, FaMoneyBill, FaGift, FaCalendarAlt } from 'react-icons/fa';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

export default function AnalyticsDashboardNew() {
  const [timeRange, setTimeRange] = useState(7);
  const [customDateRange, setCustomDateRange] = useState(false);
  const [startDate, setStartDate] = useState(moment().startOf('day').subtract(7, 'days').toDate());
  const [endDate, setEndDate] = useState(moment().endOf('day').toDate());
  const [tempStartDate, setTempStartDate] = useState(moment().startOf('day').subtract(7, 'days').toDate());
  const [tempEndDate, setTempEndDate] = useState(moment().endOf('day').toDate());
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [showMonthPicker, setShowMonthPicker] = useState(false);
  const [selectedMonthYear, setSelectedMonthYear] = useState(() => ({
    month: moment().month(),
    year: moment().year()
  }));
  const [showLoadDataModal, setShowLoadDataModal] = useState(false);
  const [loadDataScope, setLoadDataScope] = useState('day');
  const [loadDataDay, setLoadDataDay] = useState(() => moment().format('YYYY-MM-DD'));
  const [loadDataMonthYear, setLoadDataMonthYear] = useState(() => ({ month: moment().month(), year: moment().year() }));
  const [loadDataYearOnly, setLoadDataYearOnly] = useState(() => moment().year());
  const [loadDataLoading, setLoadDataLoading] = useState(false);
  const [loadDataProgress, setLoadDataProgress] = useState({ current: 0, total: 0 });
  const [loadDataError, setLoadDataError] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [dataLoading, setDataLoading] = useState(false);
  const [initialLoad, setInitialLoad] = useState(true);
  const [error, setError] = useState(null);
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  const handleOrdersClick = () => {
    navigate('/order-management');
  };

  // Event prices map: { id: priceData }; priceData.price. If all price === 0, event is free.
  const getEventFreePaid = (event) => {
    const prices = event?.prices;
    if (!prices || typeof prices !== 'object' || Array.isArray(prices)) return null;
    const entries = Object.values(prices);
    if (entries.length === 0) return true;
    return entries.every(p => Number(p?.price ?? 0) === 0);
  };

  const handleCustomersClick = () => {
    navigate('/user-management');
  };
  const [orderData, setOrderData] = useState({
    daily: [],
    total: 0,
    revenue: 0,
    totalTransactions: 0,
    growth: 0,
    revenueGrowth: 0,
    transactionGrowth: 0,
    freeOrders: 0,
    paidOrders: 0,
    dailyFreeOrders: [],
    dailyPaidOrders: [],
    ordersWithoutRates: 0
  });
  const [orderDetailsList, setOrderDetailsList] = useState([]);
  const [ordersByUserType, setOrdersByUserType] = useState({
    newUserOrders: 0,
    activeUserOrders: 0,
    unknownOrders: 0,
    total: 0,
    newPercent: 0,
    activePercent: 0
  });
  const [eventViewsByDay, setEventViewsByDay] = useState([]);
  const [customerData, setCustomerData] = useState({
    daily: [],
    new: 0,
    growth: 0,
    active: 0,
    activeGrowth: 0
  });
  const [visitData, setVisitData] = useState({
    daily: [],
    total: 0,
    unique: 0,
    growth: 0,
    platforms: {
      mobile: 0,
      desktop: 0,
      tablet: 0
    },
    withUserAgent: 0,
    withoutUserAgent: 0,
    userAgents: {},
    domains: {
      'evtgo.com': 0,
      'eventgo.ca': 0,
      'other': 0
    },
    pageTypes: {
      'event': 0,
      'host': 0,
      'other': 0
    },
    urlBreakdown: []
  });
  const [eventData, setEventData] = useState({
    daily: [],
    total: 0,
    byType: {},
    growth: 0
  });
  const [eventOrders, setEventOrders] = useState({
    events: [],
    loading: false,
    error: null
  });
  const [organizationData, setOrganizationData] = useState({
    daily: [],
    total: 0,
    new: 0,
    growth: 0,
    byType: {},
    memberCount: 0,
    eventCount: 0,
    organizations: []
  });
  const [urlPage, setUrlPage] = useState(1);
  const [createdEventsData, setCreatedEventsData] = useState({
    events: [],
    loading: false,
    error: null
  });
  const [eventModalEvent, setEventModalEvent] = useState(null);
  const [eventModalData, setEventModalData] = useState(null);
  const [eventModalLoading, setEventModalLoading] = useState(false);
  const [liveEventsInPeriod, setLiveEventsInPeriod] = useState([]);
  const [liveAndUpcomingEvents, setLiveAndUpcomingEvents] = useState([]);
  const [includeUnapprovedEvents, setIncludeUnapprovedEvents] = useState(false);
  const [eventsPaymentsLoading, setEventsPaymentsLoading] = useState(false);
  const [eventsPaymentsTotal, setEventsPaymentsTotal] = useState(null);
  const [eventsPaymentsPerEvent, setEventsPaymentsPerEvent] = useState({});
  const [eventsPaymentsProgress, setEventsPaymentsProgress] = useState({ current: 0, total: 0 });

  const visibleEventsInPeriod = useMemo(
    () =>
      includeUnapprovedEvents
        ? liveEventsInPeriod
        : liveEventsInPeriod.filter(ev => ev?.approved !== false),
    [includeUnapprovedEvents, liveEventsInPeriod]
  );

  const eventsEndingThisPeriod = useMemo(
    () => visibleEventsInPeriod,
    [visibleEventsInPeriod]
  );

  // Organizations behind events ending this period vs new organizations this period
  const orgsBehindEndingEvents = useMemo(() => {
    if (!eventsEndingThisPeriod || !eventsEndingThisPeriod.length) return [];
    const seen = new Set();
    const list = [];
    const orgsInPeriod = organizationData.organizations || [];
    eventsEndingThisPeriod.forEach((ev) => {
      const orgId = ev.orgId;
      if (!orgId || seen.has(orgId)) return;
      seen.add(orgId);
      const meta = orgsInPeriod.find((o) => o.id === orgId);
      list.push({
        id: orgId,
        name:
          meta?.name ||
          meta?.groupName ||
          ev.orgName ||
          ev.organizationName ||
          "Unknown organization",
        type: meta?.type || null,
        createdAt:
          meta?.timestamp?.toDate?.() ??
          (meta?.timestamp instanceof Date ? meta.timestamp : null),
        isNewThisPeriod: !!meta,
      });
    });
    return list;
  }, [eventsEndingThisPeriod, organizationData.organizations]);

  const newOrganizationsThisPeriod = useMemo(
    () => organizationData.organizations || [],
    [organizationData.organizations]
  );

  // Organization summary for this period
  const organizationsThisPeriodList = useMemo(() => {
    const orgs = organizationData.organizations || [];
    return orgs.filter((org) => {
      const count =
        typeof org.eventCount === "number"
          ? org.eventCount
          : typeof org.event_count === "number"
          ? org.event_count
          : 0;
      return count >= 1;
    });
  }, [organizationData.organizations]);

  const totalOrganizationsThisPeriod = organizationsThisPeriodList.length;
  const newOrganizationsCountThisPeriod = organizationsThisPeriodList.length;
  const activeOrganizationsThisPeriod = organizationsThisPeriodList.length;

  // Event orgIds that are NOT in organizationsThisPeriodList
  const eventOrgIdsNotInOrganizationsThisPeriod = useMemo(() => {
    if (!orgsBehindEndingEvents.length) return [];
    const orgsSet = new Set(organizationsThisPeriodList.map((o) => o.id));
    return orgsBehindEndingEvents.filter((o) => !orgsSet.has(o.id));
  }, [orgsBehindEndingEvents, organizationsThisPeriodList]);

  // Live & upcoming events: status = live, approved = true, endDate beyond this period
  const liveAndFutureEventsThisPeriod = useMemo(
    () => liveAndUpcomingEvents,
    [liveAndUpcomingEvents]
  );

  // Map eventId -> event title from visible events list (for Order Details by Event)
  const eventIdToTitle = useMemo(() => {
    const map = new Map();
    visibleEventsInPeriod.forEach(event => {
      const title = event.title || event.eventDetails?.title || '';
      if (event.id) map.set(event.id, title);
    });
    return map;
  }, [visibleEventsInPeriod]);

  // Overall top 20 events by total views across all days (for Event Views by Day "Overall" tab)
  const eventViewsOverallTop20 = useMemo(() => {
    const totals = new Map();
    eventViewsByDay.forEach(dayRow => {
      dayRow.topEvents.forEach(ev => {
        const prev = totals.get(ev.eventId) || 0;
        totals.set(ev.eventId, prev + (ev.count || 0));
      });
    });
    return Array.from(totals.entries())
      .map(([eventId, count]) => ({ eventId, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 20);
  }, [eventViewsByDay]);

  // Events to watch: growing popularity (more views in second half of period than first half), best 20 by growth
  const eventViewsGrowingPopularity = useMemo(() => {
    const n = eventViewsByDay.length;
    if (n < 2) return [];
    const mid = Math.floor(n / 2);
    const firstHalf = eventViewsByDay.slice(0, mid);
    const secondHalf = eventViewsByDay.slice(mid);
    const byEvent = new Map();
    firstHalf.forEach(dayRow => {
      dayRow.topEvents.forEach(ev => {
        const cur = byEvent.get(ev.eventId) || { first: 0, second: 0 };
        cur.first += ev.count || 0;
        byEvent.set(ev.eventId, cur);
      });
    });
    secondHalf.forEach(dayRow => {
      dayRow.topEvents.forEach(ev => {
        const cur = byEvent.get(ev.eventId) || { first: 0, second: 0 };
        cur.second += ev.count || 0;
        byEvent.set(ev.eventId, cur);
      });
    });
    return Array.from(byEvent.entries())
      .map(([eventId, { first, second }]) => ({ eventId, first, second, growth: second - first }))
      .filter(ev => ev.growth > 0)
      .sort((a, b) => b.growth - a.growth)
      .slice(0, 20);
  }, [eventViewsByDay]);

  // Order details grouped by event: { eventId, orderCount, amount, lastOrderDate, lastPurchaser }
  const orderDetailsByEvent = useMemo(() => {
    const byEvent = new Map();
    orderDetailsList.forEach(row => {
      const eventId = row.eventId ?? row.eventID ?? 'unknown';
      const date = row.date;
      const purchaser = row.userName || row.userEmail || row.userUID || '—';
      const amount = typeof row.amount === 'number' ? row.amount : parseFloat(row.amount) || 0;
      if (!byEvent.has(eventId)) {
        byEvent.set(eventId, { eventId, orders: [], orderCount: 0, amount: 0, lastOrderDate: null, lastPurchaser: null });
      }
      const entry = byEvent.get(eventId);
      entry.orders.push({ date, purchaser, amount, userEmail: row.userEmail, userName: row.userName, userUID: row.userUID });
      entry.orderCount = entry.orders.length;
      entry.amount += amount;
    });
    // For each event, set lastOrderDate and lastPurchaser from the latest order by date
    const result = [];
    byEvent.forEach(entry => {
      if (entry.orders.length > 0) {
        const sorted = [...entry.orders].sort((a, b) => (b.date || '').localeCompare(a.date || ''));
        entry.lastOrderDate = sorted[0].date;
        entry.lastPurchaser = sorted[0].userName || sorted[0].userEmail || sorted[0].userUID || '—';
      }
      result.push(entry);
    });
    return result.sort((a, b) => b.orderCount - a.orderCount);
  }, [orderDetailsList]);

  const orderDetailsByEventId = useMemo(() => {
    const map = new Map();
    orderDetailsByEvent.forEach(row => {
      map.set(row.eventId, { orderCount: row.orderCount, amount: row.amount });
    });
    return map;
  }, [orderDetailsByEvent]);

  const timeRanges = useMemo(() => [
    { days: 7, label: '1 Week' },
    { days: 30, label: '1 Month' },
    { days: 90, label: '3 Months' },
    { days: 180, label: '6 Months' },
    { days: 365, label: '1 Year' },
    { days: 'month', label: 'By month' },
    { days: 'custom', label: 'Custom' }
  ], []);

  const handleTimeRangeChange = useCallback((days) => {
    if (days === 'custom') {
      setTempStartDate(startDate);
      setTempEndDate(endDate);
      setShowDatePicker(true);
      return;
    }
    if (days === 'month') {
      setSelectedMonthYear(
        timeRange === 'month' && startDate
          ? { month: moment(startDate).month(), year: moment(startDate).year() }
          : { month: moment().month(), year: moment().year() }
      );
      setShowMonthPicker(true);
      return;
    }
    setCustomDateRange(false);
    setTimeRange(days);
    setStartDate(moment().startOf('day').subtract(days, 'days').toDate());
    setEndDate(moment().endOf('day').toDate());
    setError(null);
  }, [startDate, endDate, timeRange]);

  const handleApplyDateRange = useCallback(() => {
    try {
      if (!tempStartDate || !tempEndDate) {
        throw new Error('Please select both start and end dates');
      }
      if (moment(tempEndDate).isBefore(tempStartDate)) {
        throw new Error('End date must be after start date');
      }
      setShowDatePicker(false);
      setCustomDateRange(true);
      setStartDate(tempStartDate);
      setEndDate(tempEndDate);
      const diffDays = moment(tempEndDate).diff(moment(tempStartDate), 'days');
      setTimeRange(diffDays);
      setError(null);
      // Trigger data loading
      setDataLoading(true);
    } catch (err) {
      setError(err.message || 'Error setting custom date range');
      console.error('Error in handleApplyDateRange:', err);
    }
  }, [tempStartDate, tempEndDate]);

  const handleApplyMonth = useCallback(() => {
    const start = moment().month(selectedMonthYear.month).year(selectedMonthYear.year).startOf('month').toDate();
    const end = moment().month(selectedMonthYear.month).year(selectedMonthYear.year).endOf('month').toDate();
    setShowMonthPicker(false);
    setCustomDateRange(false);
    setTimeRange('month');
    setStartDate(start);
    setEndDate(end);
    setDataLoading(true);
    setError(null);
  }, [selectedMonthYear]);

  const LOAD_SUMMARY_API_BASE = 'https://v2systemapigetsummarybyday-650318705789.us-central1.run.app';

  const getLoadDataDays = useCallback(() => {
    if (loadDataScope === 'day') {
      return [loadDataDay];
    }
    if (loadDataScope === 'month') {
      const start = moment().month(loadDataMonthYear.month).year(loadDataMonthYear.year).startOf('month');
      const end = moment().month(loadDataMonthYear.month).year(loadDataMonthYear.year).endOf('month');
      const days = [];
      const d = start.clone();
      while (d.isSameOrBefore(end, 'day')) {
        days.push(d.format('YYYY-MM-DD'));
        d.add(1, 'day');
      }
      return days;
    }
    const start = moment().year(loadDataYearOnly).startOf('year');
    const end = moment().year(loadDataYearOnly).endOf('year');
    const days = [];
    const d = start.clone();
    while (d.isSameOrBefore(end, 'day')) {
      days.push(d.format('YYYY-MM-DD'));
      d.add(1, 'day');
    }
    return days;
  }, [loadDataScope, loadDataDay, loadDataMonthYear, loadDataYearOnly]);

  const handleLoadData = useCallback(async () => {
    const days = getLoadDataDays();
    setLoadDataLoading(true);
    setLoadDataError(null);
    setLoadDataProgress({ current: 0, total: days.length });
    let failed = 0;
    for (let i = 0; i < days.length; i++) {
      setLoadDataProgress({ current: i + 1, total: days.length });
      try {
        const res = await fetch(`${LOAD_SUMMARY_API_BASE}/?day=${days[i]}`);
        const data = await res.json();
        if (!res.ok || data?.status !== 'success') {
          failed += 1;
        }
      } catch (err) {
        failed += 1;
      }
    }
    setLoadDataLoading(false);
    setLoadDataProgress({ current: 0, total: 0 });
    if (failed > 0) {
      setLoadDataError(`${failed} of ${days.length} day(s) failed to load.`);
    } else {
      setShowLoadDataModal(false);
      setDataLoading(true);
    }
  }, [getLoadDataDays]);

  const handleTimeRangeButtonClick = useCallback((days) => {
    if (days === 'custom') {
      handleTimeRangeChange('custom');
      return;
    }
    if (days === 'month') {
      handleTimeRangeChange('month');
      return;
    }
    setCustomDateRange(false);
    setDataLoading(true);
    setTimeRange(days);
    setStartDate(moment().startOf('day').subtract(days, 'days').toDate());
    setEndDate(moment().endOf('day').toDate());
    setError(null);
  }, [handleTimeRangeChange]);

  useEffect(() => {
    const checkAdminStatus = async () => {
      try {
        if (!currentUser) {
          navigate('/');
          return;
        }
        const adminDoc = await fires.collection('admin').doc(currentUser.uid).get();
        if (!adminDoc.exists) {
          setIsAdmin(false);
          setLoading(false);
          return;
        }
        const adminData = adminDoc.data();
        if (!adminData.roles || !adminData.roles.includes('management')) {
          setIsAdmin(false);
          setLoading(false);
          return;
        }
        setIsAdmin(true);
        setLoading(false);
        setError(null);
      } catch (err) {
        console.error('Error checking admin status:', err);
        setError('Error verifying admin access');
        setLoading(false);
      }
    };

    checkAdminStatus();
  }, [currentUser, navigate]);

  useEffect(() => {
    if (!isAdmin || loading) return;
    
    // Load on initial mount, or when dataLoading is explicitly set to true
    if (!initialLoad && !dataLoading) return;

    const fetchData = async () => {
      try {
        setDataLoading(true);
        setError(null);
        
        if (!startDate || !endDate) {
          throw new Error('Invalid date range');
        }

        const queryStartDate = moment(startDate).startOf('day').toDate();
        const queryEndDate = moment(endDate).endOf('day').toDate();
        const previousStartDate = moment(startDate)
          .subtract(moment(endDate).diff(moment(startDate), 'days'), 'days')
          .startOf('day')
          .toDate();

        // Function to fetch summary data by date range
        // Summary collection has documents with IDs in format "yyyy-MM-dd"
        const fetchSummaryData = async (startDate, endDate) => {
          const summaryDocs = [];
          const startMoment = moment(startDate).startOf('day');
          const endMoment = moment(endDate).startOf('day');
          
          // Generate all date IDs in the range
          const dateIds = [];
          let current = startMoment.clone();
          while (current.isSameOrBefore(endMoment)) {
            dateIds.push(current.format('YYYY-MM-DD'));
            current.add(1, 'day');
          }

          // Fetch all summary documents in parallel (batch of 10 for Firestore getAll)
          const BATCH_SIZE = 10;
          for (let i = 0; i < dateIds.length; i += BATCH_SIZE) {
            const batchIds = dateIds.slice(i, i + BATCH_SIZE);
            try {
              const docRefs = batchIds.map(dateId => fires.collection('summary').doc(dateId));
              const snapshots = await Promise.all(docRefs.map(ref => ref.get()));
              
              snapshots.forEach((snapshot, index) => {
                if (snapshot.exists) {
                  const data = snapshot.data();
                  summaryDocs.push({
                    date: batchIds[index],
                    ...data
                  });
                }
              });
            } catch (error) {
              console.error(`Error fetching summary batch for dates ${batchIds.join(', ')}:`, error);
            }
          }

          // Also fetch previous period data for comparison
          const previousStartMoment = moment(previousStartDate).startOf('day');
          const previousEndMoment = moment(startDate).startOf('day').subtract(1, 'day');
          const previousDateIds = [];
          let prevCurrent = previousStartMoment.clone();
          while (prevCurrent.isSameOrBefore(previousEndMoment)) {
            previousDateIds.push(prevCurrent.format('YYYY-MM-DD'));
            prevCurrent.add(1, 'day');
          }

          for (let i = 0; i < previousDateIds.length; i += BATCH_SIZE) {
            const batchIds = previousDateIds.slice(i, i + BATCH_SIZE);
            try {
              const docRefs = batchIds.map(dateId => fires.collection('summary').doc(dateId));
              const snapshots = await Promise.all(docRefs.map(ref => ref.get()));
              
              snapshots.forEach((snapshot, index) => {
                if (snapshot.exists) {
                  const data = snapshot.data();
                  summaryDocs.push({
                    date: batchIds[index],
                    ...data
                  });
                }
              });
            } catch (error) {
              console.error(`Error fetching previous period summary batch:`, error);
            }
          }

          return summaryDocs.sort((a, b) => a.date.localeCompare(b.date));
        };

        // Function to fetch data in batches with error handling (still used for events and organizations)
        const fetchInBatches = async (collection, startDate, endDate, processData) => {
          let lastDoc = null;
          let allData = [];
          let hasMore = true;
          const BATCH_SIZE = 500;

          while (hasMore) {
            try {
              let query = fires.collection(collection)
                .where('timestamp', '>=', startDate)
                .where('timestamp', '<=', endDate)
                .orderBy('timestamp', 'asc')
                .limit(BATCH_SIZE);

              if (lastDoc) {
                query = query.startAfter(lastDoc);
              }

              const snapshot = await query.get();
              
              if (snapshot.empty) {
                hasMore = false;
                break;
              }

              const batchData = snapshot.docs.map(doc => ({
                ...doc.data(),
                id: doc.id
              }));

              allData = [...allData, ...batchData];
              lastDoc = snapshot.docs[snapshot.docs.length - 1];
              
              if (snapshot.docs.length < BATCH_SIZE) {
                hasMore = false;
              }
            } catch (indexError) {
              // Fallback: if index error, try without orderBy
              if (indexError.code === 'failed-precondition') {
                console.warn(`Index error for ${collection}, trying without orderBy...`);
                try {
                  const allDocs = await fires.collection(collection).get();
                  const filtered = allDocs.docs
                    .map(doc => ({
                      ...doc.data(),
                      id: doc.id
                    }))
                    .filter(doc => {
                      const docTimestamp = doc.timestamp?.toDate?.() || doc.timestamp;
                      return docTimestamp >= startDate && docTimestamp <= endDate;
                    })
                    .sort((a, b) => {
                      const aTime = a.timestamp?.toDate?.() || a.timestamp || 0;
                      const bTime = b.timestamp?.toDate?.() || b.timestamp || 0;
                      return aTime - bTime;
                    });
                  
                  allData = filtered;
                  hasMore = false;
                } catch (fallbackError) {
                  console.error(`Error in fallback for ${collection}:`, fallbackError);
                  hasMore = false;
                }
              } else {
                throw indexError;
              }
            }
          }

          return await processData(allData);
        };

        // Process orders data from summary collection
        // Summary structure: {orders: {totalOrders: number, totalAmount: number, freeOrderCount: number, paidOrderCount: number}}
        const processOrders = (summaryDocs) => {
          const dailyOrders = {};
          const dailyFreeOrders = {};
          const dailyPaidOrders = {};
          let previousPeriodOrders = 0;
          let currentPeriodOrders = 0;
          let previousPeriodRevenue = 0;
          let currentPeriodRevenue = 0;
          let previousPeriodTransactions = 0;
          let currentPeriodTransactions = 0;
          let freeOrders = 0;
          let paidOrders = 0;
          let ordersWithoutRates = 0; // Note: summary doesn't have fee rate info, so this will be 0
          const eventOrderMap = new Map();
          const eventRevenueMap = new Map();

          summaryDocs.forEach(summary => {
            const summaryDate = moment(summary.date, 'YYYY-MM-DD').toDate();
            const day = summary.date;
            const ordersData = summary.orders || {};
            
            const totalOrders = ordersData.totalOrders || 0;
            const totalAmount = ordersData.totalAmount || 0;
            const freeOrderCount = ordersData.freeOrderCount || 0;
            const paidOrderCount = ordersData.paidOrderCount || 0;

            // Calculate platform fee: default to 3.5% of totalAmount
            // Note: summary doesn't have individual order fee rates, so we use default 3.5%
            const platformFee = totalAmount * 0.035;

            const isInSelectedPeriod = summaryDate >= startDate && summaryDate <= endDate;

            if (!dailyOrders[day]) {
              dailyOrders[day] = { count: 0, revenue: 0, transactions: 0 };
              dailyFreeOrders[day] = { count: 0 };
              dailyPaidOrders[day] = { count: 0, revenue: 0, transactions: 0 };
            }
            
            dailyOrders[day].count += totalOrders;
            dailyOrders[day].revenue += platformFee;
            dailyOrders[day].transactions += totalAmount;
            
            dailyFreeOrders[day].count += freeOrderCount;
            dailyPaidOrders[day].count += paidOrderCount;
            dailyPaidOrders[day].revenue += platformFee;
            dailyPaidOrders[day].transactions += totalAmount;

            if (isInSelectedPeriod) {
              currentPeriodOrders += totalOrders;
              currentPeriodRevenue += platformFee;
              currentPeriodTransactions += totalAmount;
              freeOrders += freeOrderCount;
              paidOrders += paidOrderCount;
            } else if (summaryDate >= previousStartDate && summaryDate < startDate) {
              previousPeriodOrders += totalOrders;
              previousPeriodRevenue += platformFee;
              previousPeriodTransactions += totalAmount;
            }
          });

          return {
            daily: Object.entries(dailyOrders)
              .filter(([date]) => moment(date, 'YYYY-MM-DD').toDate() >= startDate)
              .map(([date, data]) => ({
                date,
                count: data.count,
                revenue: data.revenue,
                transactions: data.transactions
              })),
            total: currentPeriodOrders,
            revenue: currentPeriodRevenue,
            totalTransactions: currentPeriodTransactions,
            growth: previousPeriodOrders === 0 ? 100 : 
              ((currentPeriodOrders - previousPeriodOrders) / previousPeriodOrders) * 100,
            revenueGrowth: previousPeriodRevenue === 0 ? 100 :
              ((currentPeriodRevenue - previousPeriodRevenue) / previousPeriodRevenue) * 100,
            transactionGrowth: previousPeriodTransactions === 0 ? 100 :
              ((currentPeriodTransactions - previousPeriodTransactions) / previousPeriodTransactions) * 100,
            freeOrders,
            paidOrders,
            ordersWithoutRates, // Always 0 for summary data
            dailyFreeOrders: Object.entries(dailyFreeOrders)
              .filter(([date]) => moment(date, 'YYYY-MM-DD').toDate() >= startDate)
              .map(([date, data]) => ({
                date,
                count: data.count
              })),
            dailyPaidOrders: Object.entries(dailyPaidOrders)
              .filter(([date]) => moment(date, 'YYYY-MM-DD').toDate() >= startDate)
              .map(([date, data]) => ({
                date,
                count: data.count,
                revenue: data.revenue,
                transactions: data.transactions
              })),
            eventOrderMap, // Empty for summary data - event breakdown not available
            eventRevenueMap, // Empty for summary data - event breakdown not available
            allOrders: [] // Empty for summary data - individual orders not available
          };
        };

        // Process new customers from summary collection
        // Summary structure: {users: {activeUser: number, newRegisteredUser: number, activeUserId: [], newRegisteredUserId: []}}
        const processNewCustomers = (summaryDocs) => {
          const dailyNew = {};
          let currentPeriodNew = 0;
          let previousPeriodNew = 0;
          const currentPeriodNewUIDs = new Set();
          const previousPeriodNewUIDs = new Set();

          summaryDocs.forEach(summary => {
            const summaryDate = moment(summary.date, 'YYYY-MM-DD').toDate();
            const day = summary.date;
            const usersData = summary.users || {};
            
            const newRegisteredUser = usersData.newRegisteredUser || 0;
            const newRegisteredUserId = usersData.newRegisteredUserId || [];

            if (summaryDate >= startDate && summaryDate <= endDate) {
              currentPeriodNew += newRegisteredUser;
              newRegisteredUserId.forEach(uid => currentPeriodNewUIDs.add(uid));
              if (!dailyNew[day]) dailyNew[day] = 0;
              dailyNew[day] += newRegisteredUser;
            } else if (summaryDate >= previousStartDate && summaryDate < startDate) {
              previousPeriodNew += newRegisteredUser;
              newRegisteredUserId.forEach(uid => previousPeriodNewUIDs.add(uid));
            }
          });

          const daily = Object.entries(dailyNew)
            .filter(([date]) => moment(date, 'YYYY-MM-DD').toDate() >= startDate)
            .map(([date, count]) => ({ date, count }));

          return {
            new: currentPeriodNew,
            daily,
            currentPeriodNewUIDs,
            previousPeriodNewUIDs,
            previousPeriodNew,
            growth: previousPeriodNew === 0 ? (currentPeriodNew > 0 ? 100 : 0) :
              ((currentPeriodNew - previousPeriodNew) / previousPeriodNew) * 100
          };
        };

        // Active users from summary collection (unique users only: same uid counted once per period/day)
        // Summary structure: {users: {activeUser: number, newRegisteredUser: number, activeUserId: [], newRegisteredUserId: []}}
        // We use Set so that duplicate UIDs in activeUserId (e.g. same user, multiple visits) count as one user.
        const calculateActiveCustomers = (summaryDocs, currentPeriodNewUIDs, previousPeriodNewUIDs) => {
          const currentPeriodUIDs = new Set();   // unique UIDs in current period
          const previousPeriodUIDs = new Set();   // unique UIDs in previous period
          const dailyActiveByDay = {};           // per-day unique UIDs (Set per day)

          summaryDocs.forEach(summary => {
            const summaryDate = moment(summary.date, 'YYYY-MM-DD').toDate();
            const day = summary.date;
            const usersData = summary.users || {};
            const activeUserId = usersData.activeUserId || [];

            if (summaryDate >= startDate && summaryDate <= endDate) {
              activeUserId.forEach(uid => {
                if (uid && uid !== "unknown") {
                  currentPeriodUIDs.add(uid);
                  if (!dailyActiveByDay[day]) dailyActiveByDay[day] = new Set();
                  dailyActiveByDay[day].add(uid);
                }
              });
            } else if (summaryDate >= previousStartDate && summaryDate < startDate) {
              activeUserId.forEach(uid => {
                if (uid && uid !== "unknown") {
                  previousPeriodUIDs.add(uid);
                }
              });
            }
          });

          // Exclude new users: active users must not include anyone created in the period
          currentPeriodNewUIDs.forEach(uid => currentPeriodUIDs.delete(uid));
          previousPeriodNewUIDs.forEach(uid => previousPeriodUIDs.delete(uid));
          Object.keys(dailyActiveByDay).forEach(day => {
            currentPeriodNewUIDs.forEach(uid => dailyActiveByDay[day].delete(uid));
          });

          const currentActive = currentPeriodUIDs.size;
          const previousActive = previousPeriodUIDs.size;
          const growth = previousActive === 0 ? (currentActive > 0 ? 100 : 0) :
            ((currentActive - previousActive) / previousActive) * 100;

          const dailyActive = Object.entries(dailyActiveByDay)
            .filter(([date]) => moment(date, 'YYYY-MM-DD').toDate() >= startDate)
            .map(([date, set]) => ({ date, count: set.size }));

          return {
            active: currentActive,
            growth,
            dailyActive
          };
        };

        // Batch fetch user details using Firestore getAll (more efficient)
        const batchGetUserDetails = async (uids) => {
          const validUIDs = uids.filter(uid => uid && uid !== "unknown");
          if (validUIDs.length === 0) return new Map();
          
          const userDetailsMap = new Map();
          const BATCH_SIZE = 10; // Firestore getAll limit is 10
          
          for (let i = 0; i < validUIDs.length; i += BATCH_SIZE) {
            const batchUIDs = validUIDs.slice(i, i + BATCH_SIZE);
            try {
              const docRefs = batchUIDs.map(uid => fires.collection('users').doc(uid));
              const snapshots = await Promise.all(docRefs.map(ref => ref.get()));
              
              snapshots.forEach((snapshot, index) => {
                if (snapshot.exists) {
                  const userData = snapshot.data();
                  userDetailsMap.set(batchUIDs[index], {
                    firstName: userData.firstName || '',
                    lastName: userData.lastName || '',
                    email: userData.email || '',
                    displayName: userData.displayName || ''
                  });
                }
              });
            } catch (error) {
              console.log('Error batch fetching user details:', error);
            }
          }
          
          return userDetailsMap;
        };

        // Batch fetch event approved status (orders from events with approved = false are not counted)
        const batchGetEventApproved = async (eventIds) => {
          const validEventIds = [...new Set(eventIds.filter(id => id && id !== "unknown"))];
          if (validEventIds.length === 0) return new Map();
          const approvedMap = new Map();
          const BATCH_SIZE = 10;
          for (let i = 0; i < validEventIds.length; i += BATCH_SIZE) {
            const batchIds = validEventIds.slice(i, i + BATCH_SIZE);
            try {
              const docRefs = batchIds.map(id => fires.collection('events').doc(id));
              const snapshots = await Promise.all(docRefs.map(ref => ref.get()));
              snapshots.forEach((snapshot, index) => {
                if (snapshot.exists) {
                  const approved = snapshot.data().approved;
                  approvedMap.set(batchIds[index], approved === true);
                }
              });
            } catch (error) {
              console.log('Error batch fetching event approved:', error);
            }
          }
          return approvedMap;
        };

        // Batch fetch event details using Firestore getAll (more efficient)
        const batchGetEventDetails = async (eventIds) => {
          const validEventIds = eventIds.filter(eventId => eventId && eventId !== "unknown");
          if (validEventIds.length === 0) return new Map();
          
          const eventDetailsMap = new Map();
          const BATCH_SIZE = 10; // Firestore getAll limit is 10
          
          for (let i = 0; i < validEventIds.length; i += BATCH_SIZE) {
            const batchIds = validEventIds.slice(i, i + BATCH_SIZE);
            try {
              const docRefs = batchIds.map(eventId => fires.collection('events').doc(eventId));
              const snapshots = await Promise.all(docRefs.map(ref => ref.get()));
              
              snapshots.forEach((snapshot, index) => {
                if (snapshot.exists) {
                  const eventData = snapshot.data();
                  eventDetailsMap.set(batchIds[index], {
                    title: eventData.title || eventData.eventDetails?.title || 'Unknown Event',
                    id: batchIds[index]
                  });
                }
              });
            } catch (error) {
              console.log('Error batch fetching event details:', error);
            }
          }
          
          return eventDetailsMap;
        };

        // Process visits data from summary collection
        // Summary structure: {pageViews: {count: number}}
        // Note: Summary only has count, so detailed breakdowns (platforms, userAgents, domains, etc.) are not available
        const processVisits = async (summaryDocs) => {
          const dailyVisits = {};
          let previousPeriodVisits = 0;
          let currentPeriodVisits = 0;
          let totalPageViewsCount = 0;
          
          summaryDocs.forEach(summary => {
            const summaryDate = moment(summary.date, 'YYYY-MM-DD').toDate();
            const day = summary.date;
            const pageViewsData = summary.pageViews || {};
            const count = pageViewsData.realCount ?? 0;
            const rawCount = pageViewsData.count ?? 0;
            
            if (summaryDate >= startDate && summaryDate <= endDate) {
              currentPeriodVisits += count;
              totalPageViewsCount += rawCount;
              if (!dailyVisits[day]) {
                dailyVisits[day] = { count: 0 };
              }
              dailyVisits[day].count += count;
            } else if (summaryDate >= previousStartDate && summaryDate < startDate) {
              previousPeriodVisits += count;
            }
          });

          // Use activeUserId from users data for unique count
          const uniqueUIDs = new Set();
          summaryDocs.forEach(summary => {
            const summaryDate = moment(summary.date, 'YYYY-MM-DD').toDate();
            if (summaryDate >= startDate && summaryDate <= endDate) {
              const usersData = summary.users || {};
              const activeUserId = usersData.activeUserId || [];
              activeUserId.forEach(uid => {
                if (uid && uid !== "unknown") {
                  uniqueUIDs.add(uid);
                }
              });
            }
          });

          return {
            daily: Object.entries(dailyVisits).map(([date, data]) => ({
              date,
              count: data.count
            })),
            total: currentPeriodVisits,
            totalPageViewsCount,
            unique: uniqueUIDs.size,
            growth: previousPeriodVisits === 0 ? 100 :
              ((currentPeriodVisits - previousPeriodVisits) / previousPeriodVisits) * 100,
            platforms: {
              mobile: 0,
              desktop: 0,
              tablet: 0
            },
            withUserAgent: 0,
            withoutUserAgent: 0,
            userAgents: {},
            domains: {
              'evtgo.com': 0,
              'eventgo.ca': 0,
              'other': 0
            },
            pageTypes: {
              'event': 0,
              'host': 0,
              'hosts': 0,
              'category': 0,
              'eventgo-ai': 0,
              'create-new-event': 0,
              'order': 0,
              'about': 0,
              'other': 0
            },
            urlBreakdown: [],
            allVisits: [] // Empty for summary data - individual visits not available
          };
        };

        // Process events data: Events Overview only shows events with status = live and approved = true
        const processEvents = (events) => {
          const liveApproved = (events || []).filter(
            (e) => e.status === 'live' && e.approved === true
          );
          const dailyEvents = {};
          const eventTypes = {};
          let previousPeriodEvents = 0;
          let currentPeriodEvents = 0;

          liveApproved.forEach(event => {
            const eventDate = event.timestamp.toDate();
            const day = moment(eventDate).format('YYYY-MM-DD');
            const eventType = event.type || 'unknown';
            
            if (!dailyEvents[day]) {
              dailyEvents[day] = { count: 0, types: {} };
            }
            
            dailyEvents[day].count++;
            dailyEvents[day].types[eventType] = (dailyEvents[day].types[eventType] || 0) + 1;
            eventTypes[eventType] = (eventTypes[eventType] || 0) + 1;

            if (eventDate <= endDate) {
              currentPeriodEvents++;
            } else {
              previousPeriodEvents++;
            }
          });

          return {
            daily: Object.entries(dailyEvents)
              .filter(([date]) => moment(date).toDate() <= endDate)
              .map(([date, data]) => ({
                date,
                count: data.count,
                ...data.types
              })),
            total: currentPeriodEvents,
            byType: eventTypes,
            growth: previousPeriodEvents === 0 ? 100 :
              ((currentPeriodEvents - previousPeriodEvents) / previousPeriodEvents) * 100
          };
        };

        // Process organizations data
        const processOrganizations = (organizations) => {
          const dailyOrganizations = {};
          const organizationTypes = {};
          let previousPeriodOrganizations = 0;
          let currentPeriodOrganizations = 0;
          let totalMembers = 0;
          let totalEvents = 0;
          const organizationsInPeriod = [];

          organizations.forEach(org => {
            const orgDate = org.timestamp.toDate();
            const day = moment(orgDate).format('YYYY-MM-DD');
            const orgType = org.type || 'unknown';
            
            if (!dailyOrganizations[day]) {
              dailyOrganizations[day] = { count: 0, types: {} };
            }
            
            dailyOrganizations[day].count++;
            dailyOrganizations[day].types[orgType] = (dailyOrganizations[day].types[orgType] || 0) + 1;
            organizationTypes[orgType] = (organizationTypes[orgType] || 0) + 1;

            // Count members and events
            const memberCount = org.member_count || 0;
            const eventCount = org.event_count || 0;
            totalMembers += memberCount;
            totalEvents += eventCount;

            if (orgDate >= startDate) {
              currentPeriodOrganizations++;
              // Add to organizations list for display
              organizationsInPeriod.push({
                id: org.id,
                name: org.name || org.groupName || 'Untitled Organization',
                type: orgType,
                profileURL: org.profileURL || org.profilePicture || org.avatar,
                memberCount: memberCount,
                eventCount: eventCount,
                timestamp: org.timestamp
              });
            } else {
              previousPeriodOrganizations++;
            }
          });

          // Sort organizations by creation date (newest first)
          organizationsInPeriod.sort((a, b) => b.timestamp.toDate() - a.timestamp.toDate());

          return {
            daily: Object.entries(dailyOrganizations)
              .filter(([date]) => moment(date).toDate() >= startDate)
              .map(([date, data]) => ({
                date,
                count: data.count,
                ...data.types
              })),
            total: currentPeriodOrganizations,
            new: Object.values(dailyOrganizations).reduce((acc, curr) => acc + curr.count, 0),
            growth: previousPeriodOrganizations === 0 ? 100 :
              ((currentPeriodOrganizations - previousPeriodOrganizations) / previousPeriodOrganizations) * 100,
            byType: organizationTypes,
            memberCount: totalMembers,
            eventCount: totalEvents,
            organizations: organizationsInPeriod
          };
        };



        // Fetch summary data and other collections in parallel
        const [summaryDocs, eventsData, organizationsData] = await Promise.all([
          fetchSummaryData(previousStartDate, queryEndDate),
          fetchInBatches('events', previousStartDate, queryEndDate, processEvents),
          fetchInBatches('groups', previousStartDate, queryEndDate, processOrganizations)
        ]);

        // Process data from summary collection
        const ordersData = processOrders(summaryDocs);
        const customersData = processNewCustomers(summaryDocs);
        const visitsData = await processVisits(summaryDocs);

        // Process created events data (optimized with batch user fetching)
        const processCreatedEvents = async (events) => {
          const createdEventsInPeriod = [];
          const eventUIDs = new Set();
          
          // First pass: collect all events in period and UIDs
          for (const event of events) {
            const eventDate = event.timestamp.toDate();
            
            if (eventDate >= startDate && eventDate <= endDate) {
              const eventUID = event.fromUID || event.user?.uid;
              if (eventUID) {
                eventUIDs.add(eventUID);
              }
              
              createdEventsInPeriod.push({
                id: event.id,
                title: event.title || event.eventDetails?.title || 'Untitled Event',
                startDate: event.startDate ? event.startDate.toDate() : null,
                endDate: event.endDate ? event.endDate.toDate() : null,
                location: event.address?.fullAddress || event.location || event.eventDetails?.location || 'No location specified',
                hostInfo: null, // Will be populated below
                organizationInfo: event.user && event.user.userName ? {
                  userName: event.user.userName,
                  organizationName: event.user.organizationName || event.user.groupName || event.user.userName,
                  organizationId: event.user.organizationId || event.user.groupId || null
                } : null,
                timestamp: event.timestamp,
                status: event.status || 'unknown',
                type: event.type || 'unknown',
                eventUID: eventUID, // Store for later matching
                prices: event.prices || null
              });
            }
          }
          
          // Batch fetch all user details at once
          const userDetailsMap = await batchGetUserDetails([...eventUIDs]);
          
          // Second pass: populate host info from batch results
          createdEventsInPeriod.forEach(event => {
            if (event.eventUID && userDetailsMap.has(event.eventUID)) {
              const userData = userDetailsMap.get(event.eventUID);
              event.hostInfo = {
                uid: event.eventUID,
                userName: userData.displayName || 
                         `${userData.firstName || ''} ${userData.lastName || ''}`.trim() || 
                         userData.email || 'Unknown User'
              };
            } else if (event.eventUID) {
              event.hostInfo = {
                uid: event.eventUID,
                userName: 'Unknown User'
              };
            }
            // Remove temporary eventUID property
            delete event.eventUID;
          });
          
          // Sort by creation date (newest first)
          createdEventsInPeriod.sort((a, b) => b.timestamp.toDate() - a.timestamp.toDate());
          
          return createdEventsInPeriod;
        };

        // Fetch created events data
        const createdEvents = await fetchInBatches('events', queryStartDate, queryEndDate, processCreatedEvents);
        
        setCreatedEventsData({
          events: createdEvents,
          loading: false,
          error: null
        });

        // Process event orders after getting both orders and events data
        const eventOrderMap = ordersData.eventOrderMap;
        const eventRevenueMap = ordersData.eventRevenueMap;
        const eventIds = Array.from(eventOrderMap.keys()).filter(key => !key.includes('_lastOrder')); // Filter out date entries
        
        // Use optimized batch fetch function
        const eventDetailsMap = eventIds.length > 0 ? await batchGetEventDetails(eventIds) : new Map();
        
        // Map event details to include additional fields needed
        const enhancedEventDetailsMap = new Map();
        eventIds.forEach(eventId => {
          const baseDetails = eventDetailsMap.get(eventId);
          if (baseDetails) {
            enhancedEventDetailsMap.set(eventId, {
              title: baseDetails.title || 'Untitled Event',
              timestamp: null, // Not needed from batch fetch
              status: 'unknown' // Not needed from batch fetch
            });
          }
        });

        // Combine order counts with event details
        const eventOrdersList = Array.from(eventOrderMap.entries())
          .filter(([key]) => !key.includes('_lastOrder')) // Filter out the date entries
          .map(([eventId, orderCount]) => {
            const details = enhancedEventDetailsMap.get(eventId);
            return {
              eventId,
              orderCount,
              revenue: eventRevenueMap.get(eventId) || 0,
              transactions: eventRevenueMap.get(`${eventId}_transactions`) || 0,
              lastOrderDate: eventOrderMap.get(`${eventId}_lastOrder`),
              ...(details || {}),
            };
          })
          .filter(event => event.title) // Only include events with valid details
          .sort((a, b) => {
            // First sort by order count
            if (b.orderCount !== a.orderCount) {
              return b.orderCount - a.orderCount;
            }
            // If order counts are equal, sort by revenue
            return b.revenue - a.revenue;
          });

        setEventOrders({
          events: eventOrdersList,
          loading: false,
          error: null
        });

        // Active users from summary data, excluding new customers
        const activeCustomerData = calculateActiveCustomers(
          summaryDocs,
          customersData.currentPeriodNewUIDs || new Set(),
          customersData.previousPeriodNewUIDs || new Set()
        );

        // Merge daily new and daily active into one chart dataset (all dates in range)
        const dailyNewMap = {};
        (customersData.daily || []).forEach(({ date, count }) => { dailyNewMap[date] = count; });
        const dailyActiveMap = {};
        (activeCustomerData.dailyActive || []).forEach(({ date, count }) => { dailyActiveMap[date] = count; });
        const allDays = new Set([
          ...Object.keys(dailyNewMap),
          ...Object.keys(dailyActiveMap),
          ...(() => {
            const d = [];
            let cur = moment(startDate).startOf('day');
            const end = moment(endDate).startOf('day');
            while (cur.isSameOrBefore(end)) {
              d.push(cur.format('YYYY-MM-DD'));
              cur.add(1, 'day');
            }
            return d;
          })()
        ]);
        // Build one row per day: new users and active users are calculated by day
        const mergedDaily = Array.from(allDays)
          .filter(date => moment(date).startOf('day').toDate() >= moment(startDate).startOf('day').toDate())
          .sort((a, b) => moment(a).valueOf() - moment(b).valueOf())
          .map(date => ({
            date,
            newUsers: Number(dailyNewMap[date]) || 0,
            activeUsers: Number(dailyActiveMap[date]) || 0
          }));

        // New = sum of daily new; Active = unique active users in period (not sum of daily)
        const newTotalByDay = mergedDaily.reduce((sum, row) => sum + (Number(row.newUsers) || 0), 0);

        // Order details live inside the day document: summary doc -> orders -> orderDetails (map keyed by orderId)
        const orderDetailsAggregate = [];
        summaryDocs.forEach(summary => {
          const summaryDate = moment(summary.date, 'YYYY-MM-DD').toDate();
          if (summaryDate >= startDate && summaryDate <= endDate) {
            const orders = summary.orders;
            const orderDetails = orders?.orderDetails ?? orders?.order_details;
            if (orderDetails && typeof orderDetails === 'object' && !Array.isArray(orderDetails)) {
              Object.entries(orderDetails).forEach(([orderId, detail]) => {
                orderDetailsAggregate.push({
                  date: summary.date,
                  orderId,
                  ...(typeof detail === 'object' && detail !== null ? detail : { raw: detail })
                });
              });
            }
          }
        });
        setOrderDetailsList(orderDetailsAggregate);

        // Calculate orders breakdown by new vs active users
        // Build Set of active user UIDs from summary (users.activeUserId arrays)
        const activeUIDs = new Set();
        summaryDocs.forEach(summary => {
          const summaryDate = moment(summary.date, 'YYYY-MM-DD').toDate();
          if (summaryDate >= startDate && summaryDate <= endDate) {
            const usersData = summary.users || {};
            const activeUserId = usersData.activeUserId || [];
            activeUserId.forEach(uid => {
              if (uid && uid !== "unknown") {
                activeUIDs.add(uid);
              }
            });
          }
        });

        // Exclude new users from active users set (same logic as calculateActiveCustomers)
        const newUIDs = customersData.currentPeriodNewUIDs || new Set();
        newUIDs.forEach(uid => activeUIDs.delete(uid));

        // Count orders by user type
        let newUserOrders = 0;
        let activeUserOrders = 0;
        let unknownOrders = 0;

        orderDetailsAggregate.forEach(order => {
          const uid = order.userUID || order.userId || order.uid;
          if (!uid || uid === 'unknown') {
            unknownOrders++;
            return;
          }
          if (newUIDs.has(uid)) {
            newUserOrders++;
          } else if (activeUIDs.has(uid)) {
            activeUserOrders++;
          } else {
            unknownOrders++;
          }
        });

        const total = orderDetailsAggregate.length;
        const newPercent = total > 0 ? (newUserOrders / total) * 100 : 0;
        const activePercent = total > 0 ? (activeUserOrders / total) * 100 : 0;

        setOrdersByUserType({
          newUserOrders,
          activeUserOrders,
          unknownOrders,
          total,
          newPercent,
          activePercent
        });

        // Event views by day: summary has eventViews (map eventId -> count), top 20 per day
        const eventViewsByDayAggregate = [];
        summaryDocs.forEach(summary => {
          const summaryDate = moment(summary.date, 'YYYY-MM-DD').toDate();
          if (summaryDate >= startDate && summaryDate <= endDate) {
            const eventViews = summary.eventViews ?? summary.pageViews?.eventViews;
            if (eventViews && typeof eventViews === 'object' && !Array.isArray(eventViews)) {
              const entries = Object.entries(eventViews).map(([eventId, count]) => ({
                eventId,
                count: typeof count === 'number' ? count : parseInt(count, 10) || 0
              }));
              const top20 = entries
                .sort((a, b) => b.count - a.count)
                .slice(0, 20);
              eventViewsByDayAggregate.push({
                date: summary.date,
                topEvents: top20
              });
            } else {
              eventViewsByDayAggregate.push({ date: summary.date, topEvents: [] });
            }
          }
        });
        eventViewsByDayAggregate.sort((a, b) => a.date.localeCompare(b.date));
        setEventViewsByDay(eventViewsByDayAggregate);

        // Fetch live events whose end date falls within the period (regardless of approved status)
        try {
          let liveEvents = [];
          const periodEnd = moment(queryEndDate).endOf('day').toDate();
          try {
            const eventsSnap = await fires
              .collection('events')
              .where('status', '==', 'live')
              .where('endDate', '>=', queryStartDate)
              .where('endDate', '<=', periodEnd)
              .orderBy('endDate', 'asc')
              .get();
            liveEvents = eventsSnap.docs.map(doc => ({
              id: doc.id,
              ...doc.data()
            }));
          } catch (indexErr) {
            if (indexErr.code === 'failed-precondition' || indexErr.code === 'unavailable') {
              const allLiveSnap = await fires
                .collection('events')
                .where('status', '==', 'live')
                .get();
              const allLive = allLiveSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
              liveEvents = allLive.filter(event => {
                if (event.type !== 'live-event' && event.type !== 'event') return false;
                const end = event.endDate?.toDate?.() ?? event.endDate;
                const d = end instanceof Date ? end : end ? new Date(end) : null;
                if (!d || isNaN(d.getTime())) return false;
                return d >= queryStartDate && d <= periodEnd;
              });
              liveEvents.sort((a, b) => {
                const endA = a.endDate?.toDate?.() ?? a.endDate;
                const endB = b.endDate?.toDate?.() ?? b.endDate;
                const ta = endA instanceof Date ? endA.getTime() : endA ? new Date(endA).getTime() : 0;
                const tb = endB instanceof Date ? endB.getTime() : endB ? new Date(endB).getTime() : 0;
                return ta - tb;
              });
            } else {
              throw indexErr;
            }
          }
          setLiveEventsInPeriod(liveEvents);
        } catch (err) {
          console.warn('Failed to fetch live events in period:', err);
          setLiveEventsInPeriod([]);
        }

        // Fetch live & upcoming events whose end date is beyond this period and approved = true
        try {
          let upcoming = [];
          const periodEnd = moment(queryEndDate).endOf('day').toDate();
          try {
            const upcomingSnap = await fires
              .collection('events')
              .where('status', '==', 'live')
              .where('approved', '==', true)
              .where('endDate', '>', periodEnd)
              .orderBy('endDate', 'asc')
              .get();
            upcoming = upcomingSnap.docs.map(doc => ({
              id: doc.id,
              ...doc.data()
            }));
          } catch (indexErr) {
            if (indexErr.code === 'failed-precondition' || indexErr.code === 'unavailable') {
              const allLiveSnap = await fires
                .collection('events')
                .where('status', '==', 'live')
                .get();
              const allLive = allLiveSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
              upcoming = allLive.filter(event => {
                if (event.type !== 'live-event' && event.type !== 'event') return false;
                if (event.approved !== true) return false;
                const end = event.endDate?.toDate?.() ?? event.endDate;
                const d = end instanceof Date ? end : end ? new Date(end) : null;
                if (!d || isNaN(d.getTime())) return false;
                return d > periodEnd;
              });
              upcoming.sort((a, b) => {
                const endA = a.endDate?.toDate?.() ?? a.endDate;
                const endB = b.endDate?.toDate?.() ?? b.endDate;
                const ta = endA instanceof Date ? endA.getTime() : endA ? new Date(endA).getTime() : 0;
                const tb = endB instanceof Date ? endB.getTime() : endB ? new Date(endB).getTime() : 0;
                return ta - tb;
              });
            } else {
              throw indexErr;
            }
          }
          setLiveAndUpcomingEvents(upcoming);
        } catch (err) {
          console.warn('Failed to fetch live & upcoming events:', err);
          setLiveAndUpcomingEvents([]);
        }

        setOrderData(ordersData);
        setCustomerData({
          new: newTotalByDay,
          daily: mergedDaily,
          growth: customersData.growth,
          active: activeCustomerData.active,
          activeGrowth: activeCustomerData.growth
        });
        setVisitData(visitsData);
        setEventData(eventsData);
        setOrganizationData(prevData => ({
          ...organizationsData,
          organizations: organizationsData.organizations || prevData.organizations || []
        }));

      } catch (err) {
        console.error('Error fetching analytics data:', err);
        setError('Error loading analytics data. Please try again.');
        setEventOrders(prev => ({ ...prev, loading: false, error: err.message }));
      } finally {
        setDataLoading(false);
      }
    };

    if (initialLoad || dataLoading) {
      fetchData();
      if (initialLoad) {
        setInitialLoad(false);
      }
    }
  }, [dataLoading, isAdmin, loading, initialLoad, startDate, endDate]); // Include dates in dependency but only fetch when dataLoading is true or initial load

  const EVENT_ANALYTICS_API_BASE = 'https://v2serviceapishoweventdataanalytics-650318705789.us-central1.run.app';

  // Fetch event summary for modal from external API (auth_id = uid, event_id = eventId)
  useEffect(() => {
    if (!eventModalEvent?.id) {
      setEventModalData(null);
      return;
    }
    const eventId = eventModalEvent.id;
    const uid = currentUser?.uid;
    setEventModalLoading(true);
    setEventModalData(null);
    (async () => {
      try {
        if (!uid) {
          setEventModalData({ error: 'Not signed in.' });
          setEventModalLoading(false);
          return;
        }
        const params = new URLSearchParams({ auth_id: uid, event_id: eventId });
        const res = await fetch(`${EVENT_ANALYTICS_API_BASE}/?${params.toString()}`);
        const json = await res.json();
        if (!res.ok || !json?.success || !json?.displayData) {
          setEventModalData({
            error: json?.message || 'Failed to load event analytics',
            eventName: eventModalEvent?.title,
            totalOrders: 0,
            totalOrderAmount: 0,
            totalViews: 0,
            totalRealViews: 0,
            totalOrderConversionRates: 0,
            totalSpendingRates: 0
          });
          setEventModalLoading(false);
          return;
        }
        const d = json.displayData;
        setEventModalData({
          eventName: d.eventName ?? eventModalEvent?.title,
          totalOrders: d.totalOrders ?? 0,
          totalOrderAmount: d.totalOrderAmount ?? 0,
          totalViews: d.totalViews ?? 0,
          totalRealViews: d.totalRealViews ?? 0,
          totalOrderConversionRates: d.totalOrderConversionRates ?? 0,
          totalSpendingRates: d.totalSpendingRates ?? 0
        });
      } catch (err) {
        console.error('Error fetching event analytics:', err);
        setEventModalData({
          error: err?.message || 'Failed to load event analytics',
          eventName: eventModalEvent?.title,
          totalOrders: 0,
          totalOrderAmount: 0,
          totalViews: 0,
          totalRealViews: 0,
          totalOrderConversionRates: 0,
          totalSpendingRates: 0
        });
      }
      setEventModalLoading(false);
    })();
  }, [eventModalEvent?.id, currentUser?.uid]);

  const getChartData = (data, valueKey = 'count') => {
    return data.map(item => ({
      date: moment(item.date).format('MM/DD'),
      [valueKey]: item[valueKey]
    }));
  };

  const getPlatformChartData = (platforms) => {
    return [
      { name: 'Mobile', value: platforms.mobile },
      { name: 'Desktop', value: platforms.desktop },
      { name: 'Tablet', value: platforms.tablet }
    ];
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-2 border rounded shadow-sm">
          <p className="mb-0">{`Date: ${label}`}</p>
          {payload.map((entry, index) => (
            <p key={index} className="mb-0" style={{ color: entry.color }}>
              {`${entry.name}: ${entry.dataKey === 'revenue' || entry.dataKey === 'transactions' ? 
                `$${entry.value.toFixed(2)}` : entry.value}`}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  const GrowthBadge = ({ value }) => (
    <Badge bg={value >= 0 ? 'success' : 'danger'} className="ms-2">
      {value >= 0 ? '↑' : '↓'} {Math.abs(value).toFixed(1)}%
    </Badge>
  );

  const LoadingOverlay = () => (
    <div 
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(255, 255, 255, 0.8)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        borderRadius: 'inherit'
      }}
    >
      <div className="text-center">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <div className="mt-2">Updating data...</div>
      </div>
    </div>
  );

  const CardWithLoading = ({ children, showLoading = true }) => (
    <div style={{ position: 'relative' }}>
      {children}
      {dataLoading && showLoading && <LoadingOverlay />}
    </div>
  );

  const DateRangePickerModal = useCallback(() => (
    <Modal 
      show={showDatePicker} 
      onHide={() => {
        setShowDatePicker(false);
        setError(null);
      }}
    >
      <Modal.Header closeButton>
        <Modal.Title>Select Date Range</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && (
          <Alert variant="danger" className="mb-3">
            {error}
          </Alert>
        )}
        <Form>
          <Form.Group className="mb-3">
            <Form.Label>Start Date</Form.Label>
            <div>
              <DatePicker
                selected={tempStartDate}
                onChange={date => {
                  setTempStartDate(moment(date).startOf('day').toDate());
                  setError(null);
                }}
                selectsStart
                startDate={tempStartDate}
                endDate={tempEndDate}
                maxDate={tempEndDate}
                dateFormat="MMMM d, yyyy"
                className="form-control"
                placeholderText="Select start date"
              />
            </div>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>End Date</Form.Label>
            <div>
              <DatePicker
                selected={tempEndDate}
                onChange={date => {
                  setTempEndDate(moment(date).endOf('day').toDate());
                  setError(null);
                }}
                selectsEnd
                startDate={tempStartDate}
                endDate={tempEndDate}
                minDate={tempStartDate}
                maxDate={new Date()}
                dateFormat="MMMM d, yyyy"
                className="form-control"
                placeholderText="Select end date"
              />
            </div>
          </Form.Group>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button 
          variant="secondary" 
          onClick={() => {
            setShowDatePicker(false);
            setError(null);
          }}
        >
          Cancel
        </Button>
        <Button 
          variant="primary" 
          onClick={handleApplyDateRange}
          disabled={!tempStartDate || !tempEndDate || dataLoading}
        >
          {dataLoading ? (
            <>
              <Spinner
                as="span"
                animation="border"
                size="sm"
                role="status"
                aria-hidden="true"
                className="me-2"
              />
              Loading...
            </>
          ) : (
            'Apply'
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  ), [showDatePicker, tempStartDate, tempEndDate, dataLoading, error, handleApplyDateRange]);

  const MonthPickerModal = useCallback(() => {
    const months = moment.monthsShort(); // Jan, Feb, ...
    const currentYear = moment().year();
    const years = Array.from({ length: 5 }, (_, i) => currentYear - 2 + i);
    return (
      <Modal
        show={showMonthPicker}
        onHide={() => setShowMonthPicker(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Select month</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Month</Form.Label>
              <Form.Select
                value={selectedMonthYear.month}
                onChange={(e) => setSelectedMonthYear(prev => ({ ...prev, month: parseInt(e.target.value, 10) }))}
              >
                {months.map((name, i) => (
                  <option key={i} value={i}>{name}</option>
                ))}
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Year</Form.Label>
              <Form.Select
                value={selectedMonthYear.year}
                onChange={(e) => setSelectedMonthYear(prev => ({ ...prev, year: parseInt(e.target.value, 10) }))}
              >
                {years.map(y => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </Form.Select>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowMonthPicker(false)}>Cancel</Button>
          <Button variant="primary" onClick={handleApplyMonth}>
            Apply
          </Button>
        </Modal.Footer>
      </Modal>
    );
  }, [showMonthPicker, selectedMonthYear, handleApplyMonth]);

  const LoadDataModal = useCallback(() => {
    const months = moment.monthsShort();
    const currentYear = moment().year();
    const years = Array.from({ length: 6 }, (_, i) => currentYear - 2 + i);
    return (
      <Modal show={showLoadDataModal} onHide={() => !loadDataLoading && setShowLoadDataModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Load summary data</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {loadDataError && (
            <Alert variant="danger" dismissible onClose={() => setLoadDataError(null)} className="mb-3">
              {loadDataError}
            </Alert>
          )}
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Load for</Form.Label>
              <Form.Select
                value={loadDataScope}
                onChange={(e) => setLoadDataScope(e.target.value)}
                disabled={loadDataLoading}
              >
                <option value="day">Day</option>
                <option value="month">Month</option>
                <option value="year">Year</option>
              </Form.Select>
            </Form.Group>
            {loadDataScope === 'day' && (
              <Form.Group className="mb-3">
                <Form.Label>Date</Form.Label>
                <Form.Control
                  type="date"
                  value={loadDataDay}
                  onChange={(e) => setLoadDataDay(e.target.value)}
                  disabled={loadDataLoading}
                />
              </Form.Group>
            )}
            {loadDataScope === 'month' && (
              <>
                <Form.Group className="mb-3">
                  <Form.Label>Month</Form.Label>
                  <Form.Select
                    value={loadDataMonthYear.month}
                    onChange={(e) => setLoadDataMonthYear(prev => ({ ...prev, month: parseInt(e.target.value, 10) }))}
                    disabled={loadDataLoading}
                  >
                    {months.map((name, i) => (
                      <option key={i} value={i}>{name}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Year</Form.Label>
                  <Form.Select
                    value={loadDataMonthYear.year}
                    onChange={(e) => setLoadDataMonthYear(prev => ({ ...prev, year: parseInt(e.target.value, 10) }))}
                    disabled={loadDataLoading}
                  >
                    {years.map(y => (
                      <option key={y} value={y}>{y}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </>
            )}
            {loadDataScope === 'year' && (
              <Form.Group className="mb-3">
                <Form.Label>Year</Form.Label>
                <Form.Select
                  value={loadDataYearOnly}
                  onChange={(e) => setLoadDataYearOnly(parseInt(e.target.value, 10))}
                  disabled={loadDataLoading}
                >
                  {years.map(y => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            )}
          </Form>
          {loadDataLoading && loadDataProgress.total > 0 && (
            <p className="text-muted small mb-0 mt-2">
              Loading... {loadDataProgress.current} / {loadDataProgress.total}
            </p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowLoadDataModal(false)} disabled={loadDataLoading}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleLoadData}
            disabled={loadDataLoading}
          >
            {loadDataLoading ? (
              <>
                <Spinner as="span" animation="border" size="sm" className="me-2" />
                Load
              </>
            ) : (
              'Load'
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    );
  }, [showLoadDataModal, loadDataScope, loadDataDay, loadDataMonthYear, loadDataYearOnly, loadDataLoading, loadDataProgress, loadDataError, handleLoadData]);

  // Add responsive chart height calculation
  const getChartHeight = useCallback(() => {
    return window.innerWidth < 768 ? 200 : 300;
  }, []);

  const [chartHeight, setChartHeight] = useState(getChartHeight());

  useEffect(() => {
    const handleResize = () => {
      setChartHeight(getChartHeight());
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [getChartHeight]);

  const EventOrdersList = () => {
    const [sortField, setSortField] = useState('orderCount');
    const [sortDirection, setSortDirection] = useState('desc');

    const handleSort = (field) => {
      if (sortField === field) {
        setSortDirection(sortDirection === 'desc' ? 'asc' : 'desc');
      } else {
        setSortField(field);
        setSortDirection('desc');
      }
    };

    const sortedEvents = [...eventOrders.events].sort((a, b) => {
      const multiplier = sortDirection === 'desc' ? 1 : -1;
      if (sortField === 'orderCount') {
        return (b.orderCount - a.orderCount) * multiplier;
      } else if (sortField === 'revenue') {
        return (b.revenue - a.revenue) * multiplier;
      } else if (sortField === 'transactions') {
        return (b.transactions - a.transactions) * multiplier;
      } else if (sortField === 'lastOrderDate') {
        return (b.lastOrderDate - a.lastOrderDate) * multiplier;
      }
      return 0;
    });

    const SortIcon = ({ field }) => {
      if (sortField !== field) return <span className="ms-1 text-muted">↕</span>;
      return (
        <span className="ms-1">
          {sortDirection === 'desc' ? '↓' : '↑'}
        </span>
      );
    };

    return (
      <Card className="shadow-sm">
        <Card.Body className="p-2 p-md-3">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="mb-0 fs-6 fs-md-5">Event Orders</h5>
            <Badge bg="primary" className="fs-7">
              {eventOrders.events.length} Events
            </Badge>
          </div>
          <div className="table-responsive">
            <Table hover size="sm" className="mb-0">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Event</th>
                  <th 
                    className="text-center" 
                    style={{ cursor: 'pointer' }}
                    onClick={() => handleSort('orderCount')}
                  >
                    Orders
                    <SortIcon field="orderCount" />
                  </th>
                  <th 
                    className="text-end" 
                    style={{ cursor: 'pointer' }}
                    onClick={() => handleSort('transactions')}
                  >
                    Transactions
                    <SortIcon field="transactions" />
                  </th>
                  <th 
                    className="text-end" 
                    style={{ cursor: 'pointer' }}
                    onClick={() => handleSort('lastOrderDate')}
                  >
                    Last Order
                    <SortIcon field="lastOrderDate" />
                  </th>
                  <th className="text-end">Created</th>
                </tr>
              </thead>
              <tbody>
                {sortedEvents.map((event, index) => (
                  <tr key={event.eventId}>
                    <td className="text-muted">{index + 1}</td>
                    <td className="text-truncate" style={{ maxWidth: '200px' }}>
                      {event.title}
                    </td>
                    <td className="text-center">
                      <Badge bg="success">{event.orderCount}</Badge>
                    </td>
                    <td className="text-end">
                      <span className={event.revenue > 0 ? 'text-success' : 'text-muted'}>
                        ${event.transactions ? event.transactions.toFixed(2) : '0.00'}
                      </span>
                    </td>
                    <td className="text-end small">
                      {event.lastOrderDate ? moment(event.lastOrderDate).format('MM/DD/YY HH:mm') : 'N/A'}
                    </td>
                    <td className="text-end small">
                      {event.timestamp ? moment(event.timestamp.toDate()).format('MM/DD/YY') : 'N/A'}
                    </td>
                  </tr>
                ))}
                {eventOrders.events.length === 0 && (
                  <tr>
                    <td colSpan="6" className="text-center text-muted py-3">
                      No events with orders in this period
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        </Card.Body>
      </Card>
    );
  };

  const CreatedEventsList = () => {
    const [sortField, setSortField] = useState('timestamp');
    const [sortDirection, setSortDirection] = useState('desc');
    // Default: show only events with status 'live'; option to show all
    const [statusFilter, setStatusFilter] = useState('live');

    const handleSort = (field) => {
      if (sortField === field) {
        setSortDirection(sortDirection === 'desc' ? 'asc' : 'desc');
      } else {
        setSortField(field);
        setSortDirection('desc');
      }
    };

    const filteredEvents = statusFilter === 'live'
      ? createdEventsData.events.filter((e) => (e.status || '').toLowerCase() === 'live')
      : createdEventsData.events;

    const sortedEvents = [...filteredEvents].sort((a, b) => {
      const multiplier = sortDirection === 'desc' ? 1 : -1;
      if (sortField === 'timestamp') {
        return (b.timestamp.toDate() - a.timestamp.toDate()) * multiplier;
      } else if (sortField === 'startDate') {
        if (!a.startDate && !b.startDate) return 0;
        if (!a.startDate) return 1;
        if (!b.startDate) return -1;
        return (b.startDate - a.startDate) * multiplier;
      } else if (sortField === 'title') {
        return a.title.localeCompare(b.title) * multiplier;
      } else if (sortField === 'status') {
        return a.status.localeCompare(b.status) * multiplier;
      }
      return 0;
    });

    const SortIcon = ({ field }) => {
      if (sortField !== field) return <span className="ms-1 text-muted">↕</span>;
      return (
        <span className="ms-1">
          {sortDirection === 'desc' ? '↓' : '↑'}
        </span>
      );
    };

    return (
      <Card className="shadow-sm">
        <Card.Body className="p-2 p-md-3">
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
            <h5 className="mb-0 fs-6 fs-md-5">Created Events</h5>
            <div className="d-flex align-items-center gap-2 flex-wrap">
              <Form.Select
                size="sm"
                style={{ width: 'auto', minWidth: '100px' }}
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="fs-7"
              >
                <option value="live">Live only</option>
                <option value="all">All</option>
              </Form.Select>
              <Badge bg="success" className="fs-7">
                {filteredEvents.length} Events
              </Badge>
              {(() => {
                const statusCounts = {};
                filteredEvents.forEach(event => {
                  statusCounts[event.status] = (statusCounts[event.status] || 0) + 1;
                });
                return Object.entries(statusCounts).map(([status, count]) => (
                  <Badge 
                    key={status}
                    bg={status === 'active' || status === 'live' ? 'success' : 
                        status === 'draft' ? 'warning' : 
                        status === 'private' ? 'dark' :
                        status === 'cancelled' ? 'danger' : 
                        status === 'published' ? 'primary' :
                        status === 'pending' ? 'info' : 'secondary'} 
                    className="fs-7"
                  >
                    {status}: {count}
                  </Badge>
                ));
              })()}
              {(() => {
                let freeCount = 0;
                let paidCount = 0;
                filteredEvents.forEach(event => {
                  const fp = getEventFreePaid(event);
                  if (fp === true) freeCount++;
                  else if (fp === false) paidCount++;
                });
                return (
                  <>
                    <Badge bg="success" className="fs-7">Free: {freeCount}</Badge>
                    <Badge bg="primary" className="fs-7">Paid: {paidCount}</Badge>
                  </>
                );
              })()}
            </div>
          </div>
          <div className="table-responsive">
            <Table hover size="sm" className="mb-0">
                              <thead>
                  <tr>
                    <th>#</th>
                    <th>Event ID</th>
                    <th 
                      style={{ cursor: 'pointer' }}
                      onClick={() => handleSort('title')}
                    >
                      Event Name
                      <SortIcon field="title" />
                    </th>
                    <th 
                      className="text-center" 
                      style={{ cursor: 'pointer' }}
                      onClick={() => handleSort('status')}
                    >
                      Status
                      <SortIcon field="status" />
                    </th>
                    <th 
                      className="text-center" 
                      style={{ cursor: 'pointer' }}
                      onClick={() => handleSort('startDate')}
                    >
                      Start Date
                      <SortIcon field="startDate" />
                    </th>
                    <th className="text-center">End Date</th>
                    <th className="text-center">Free/Paid</th>
                    <th>Location</th>
                    <th>Host</th>
                    <th>Organization</th>
                    <th 
                      className="text-end" 
                      style={{ cursor: 'pointer' }}
                      onClick={() => handleSort('timestamp')}
                    >
                      Created
                      <SortIcon field="timestamp" />
                    </th>
                  </tr>
                </thead>
                              <tbody>
                  {sortedEvents.map((event, index) => (
                    <tr key={event.id}>
                      <td className="text-muted">{index + 1}</td>
                      <td>
                        <div className="small text-muted font-monospace">{event.id}</div>
                      </td>
                      <td>
                        <button
                          type="button"
                          className="btn btn-link p-0 fw-bold small link-primary text-decoration-none"
                          onClick={() => setEventModalEvent({ id: event.id, title: event.title })}
                        >
                          {event.title}
                        </button>
                        {event.type !== 'unknown' && (
                          <div className="text-muted small">
                            <Badge bg="info" className="fs-7">
                              {event.type}
                            </Badge>
                          </div>
                        )}
                      </td>
                      <td className="text-center">
                        <Badge bg={event.status === 'active' || event.status === 'live' ? 'success' : 
                                   event.status === 'draft' ? 'warning' : 
                                   event.status === 'private' ? 'dark' :
                                   event.status === 'cancelled' ? 'danger' : 
                                   event.status === 'published' ? 'primary' :
                                   event.status === 'pending' ? 'info' : 'secondary'} 
                               className="fs-7">
                          {event.status}
                        </Badge>
                      </td>
                      <td className="text-center small">
                        {event.startDate ? moment(event.startDate).format('MM/DD/YY') : 'N/A'}
                      </td>
                      <td className="text-center small">
                        {event.endDate ? moment(event.endDate).format('MM/DD/YY') : 'N/A'}
                      </td>
                      <td className="text-center">
                        {getEventFreePaid(event) === true ? (
                          <Badge bg="success" className="fs-7">Free</Badge>
                        ) : getEventFreePaid(event) === false ? (
                          <Badge bg="primary" className="fs-7">Paid</Badge>
                        ) : (
                          <span className="text-muted">—</span>
                        )}
                      </td>
                      <td>
                        <div className="small text-truncate" style={{ maxWidth: '150px' }}>
                          {event.location}
                        </div>
                      </td>
                      <td>
                        {event.hostInfo ? (
                          <div>
                            <div className="fw-bold small">{event.hostInfo.userName}</div>
                            <div className="text-muted small">UID: {event.hostInfo.uid}</div>
                          </div>
                        ) : (
                          <span className="text-muted small">Unknown</span>
                        )}
                      </td>
                      <td>
                        {event.organizationInfo ? (
                          <div>
                            <div className="fw-bold small">{event.organizationInfo.organizationName}</div>
                            {event.organizationInfo.organizationId && (
                              <div className="text-muted small">ID: {event.organizationInfo.organizationId}</div>
                            )}
                          </div>
                        ) : (
                          <span className="text-muted small">-</span>
                        )}
                      </td>
                      <td className="text-end small">
                        {moment(event.timestamp.toDate()).format('MM/DD/YY HH:mm')}
                      </td>
                    </tr>
                  ))}
                  {filteredEvents.length === 0 && (
                    <tr>
                      <td colSpan="11" className="text-center text-muted py-3">
                        {statusFilter === 'live'
                          ? 'No live events in this period. Switch to "All" to see other statuses.'
                          : 'No events created in this period'}
                      </td>
                    </tr>
                  )}
                </tbody>
            </Table>
          </div>
        </Card.Body>
      </Card>
    );
  };

  if (loading) {
    return (
      <Container fluid className="p-4" style={{ backgroundColor: '#f8f9fa' }}>
        <Card className="shadow-sm">
          <Card.Body>
            <div className="text-center">
              <Spinner animation="border" role="status" className="me-2" />
              <span>Loading...</span>
            </div>
          </Card.Body>
        </Card>
      </Container>
    );
  }

  if (!isAdmin) {
    return (
      <Container fluid className="p-4" style={{ backgroundColor: '#f8f9fa' }}>
        <Card className="shadow-sm">
          <Card.Body>
            <Alert variant="danger">
              <Alert.Heading>Unauthorized Access</Alert.Heading>
              <p>
                You do not have permission to view this page. Please contact your administrator if you believe this is a mistake.
              </p>
              <hr />
              <div className="d-flex justify-content-end">
                <Button variant="outline-danger" onClick={() => navigate('/')}>
                  Return to Home
                </Button>
              </div>
            </Alert>
          </Card.Body>
        </Card>
      </Container>
    );
  }

  return (
    <Container fluid className="p-2 p-md-4" style={{ backgroundColor: '#f8f9fa' }}>
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-4">
        <div className="mb-3 mb-md-0">
          <h2 className="mb-0 fs-4 fs-md-2">Analytics Preview</h2>
          <small className="text-muted d-block">
            {timeRange === 'month'
              ? moment(startDate).format('MMM YYYY')
              : `${moment(startDate).format('MMM D, YYYY')} - ${moment(endDate).format('MMM D, YYYY')}`}
            {customDateRange && ' (Custom Range)'}
          </small>
        </div>
        <Stack direction="horizontal" gap={2} className="flex-wrap justify-content-start justify-content-md-end align-items-center">
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => setShowLoadDataModal(true)}
            className="mb-2 mb-md-0"
          >
            Load data
          </Button>
          <Button
            variant="outline-success"
            size="sm"
            onClick={() => navigate('/analytics')}
            className="mb-2 mb-md-0"
          >
            Live Mode
          </Button>
          {timeRanges.map(range => (
            <Button
              key={range.days}
              size="sm"
              variant={
                range.days === 'custom' && customDateRange
                  ? 'primary'
                  : range.days === 'month' && timeRange === 'month'
                  ? 'primary'
                  : timeRange === range.days && !customDateRange
                  ? 'primary'
                  : 'outline-primary'
              }
              onClick={() => handleTimeRangeButtonClick(range.days)}
              className="px-2 px-md-3 mb-2 mb-md-0"
              disabled={dataLoading}
            >
              {dataLoading && range.days === timeRange ? (
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="me-1"
                />
              ) : range.days === 'custom' ? (
                <FaCalendarAlt className="me-1" />
              ) : range.days === 'month' ? (
                <FaCalendarAlt className="me-1" />
              ) : null}
              {range.label}
            </Button>
          ))}
        </Stack>
      </div>

      {error && (
        <Alert variant="danger" className="mb-4" onClose={() => setError(null)} dismissible>
          {error}
        </Alert>
      )}

      <Row className="g-3">
        <Col xs={12} md={4}>
          <CardWithLoading>
            <Card 
              className="shadow-sm h-100" 
              style={{ cursor: 'pointer' }}
              onClick={handleOrdersClick}
            >
              <Card.Body className="p-2 p-md-3">
                <div className="d-flex align-items-center mb-3">
                  <div className="rounded-circle p-2 p-md-3 bg-primary bg-opacity-10">
                    <FaShoppingCart className="text-primary" size={20} />
                  </div>
                  <div className="ms-2 ms-md-3">
                    <h6 className="mb-0">Orders</h6>
                    <h3 className="mb-0 fs-4 fs-md-3">
                      {orderData.total}
                      <GrowthBadge value={orderData.growth} />
                    </h3>
                  </div>
                </div>
                <div className="d-flex justify-content-between mb-3">
                  <div>
                    <div className="d-flex align-items-center">
                      <FaMoneyBill className="text-success me-1 me-md-2" size={14} />
                      <span className="small">Paid: {orderData.paidOrders}</span>
                    </div>
                  </div>
                  <div>
                    <div className="d-flex align-items-center">
                      <FaGift className="text-warning me-1 me-md-2" size={14} />
                      <span className="small">Free: {orderData.freeOrders}</span>
                    </div>
                  </div>
                </div>
                {ordersByUserType.total > 0 && (
                  <div className="mb-3 pt-2 border-top">
                    <div className="small text-muted mb-2">Orders by User Type</div>
                    <div className="d-flex justify-content-between align-items-center mb-1">
                      <div className="d-flex align-items-center">
                        <FaUsers className="text-success me-1" size={12} />
                        <span className="small">New Users:</span>
                      </div>
                      <div>
                        <span className="small fw-semibold">{ordersByUserType.newUserOrders}</span>
                        <span className="small text-muted ms-1">({ordersByUserType.newPercent.toFixed(1)}%)</span>
                      </div>
                    </div>
                    <div className="d-flex justify-content-between align-items-center mb-1">
                      <div className="d-flex align-items-center">
                        <FaUsers className="text-primary me-1" size={12} />
                        <span className="small">Active Users:</span>
                      </div>
                      <div>
                        <span className="small fw-semibold">{ordersByUserType.activeUserOrders}</span>
                        <span className="small text-muted ms-1">({ordersByUserType.activePercent.toFixed(1)}%)</span>
                      </div>
                    </div>
                    {ordersByUserType.unknownOrders > 0 && (
                      <div className="d-flex justify-content-between align-items-center">
                        <span className="small text-muted">Unknown:</span>
                        <span className="small text-muted">{ordersByUserType.unknownOrders}</span>
                      </div>
                    )}
                  </div>
                )}
                <ResponsiveContainer width="100%" height={180}>
                  <LineChart data={getChartData(orderData.daily)}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                    <YAxis 
                      tick={{ fontSize: 12 }} 
                      tickFormatter={(value) => value.toFixed(0)}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend wrapperStyle={{ fontSize: '12px' }} />
                    <Line
                      type="monotone"
                      dataKey="count"
                      name="Orders"
                      stroke="#0d6efd"
                      strokeWidth={2}
                      dot={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </Card.Body>
            </Card>
          </CardWithLoading>
        </Col>

        <Col xs={12} md={4}>
          <CardWithLoading>
            <Card 
              className="shadow-sm h-100" 
              style={{ cursor: 'pointer' }}
            >
              <Card.Body className="p-2 p-md-3">
                <div className="d-flex align-items-center mb-3">
                  <div className="rounded-circle p-2 p-md-3 bg-success bg-opacity-10">
                    <FaUsers className="text-success" size={20} />
                  </div>
                  <div className="ms-2 ms-md-3 flex-grow-1">
                    <h6 className="mb-0">Customers</h6>
                    <div className="d-flex align-items-center flex-wrap gap-2 mt-1">
                      <span>
                        <strong>New:</strong> {customerData.new ?? 0}
                        <GrowthBadge value={customerData.growth} />
                      </span>
                      <span className="text-muted">|</span>
                      <span>
                        <strong>Actives:</strong> {customerData.active ?? 0}
                        <GrowthBadge value={customerData.activeGrowth} />
                      </span>
                    </div>
                  </div>
                </div>
                <ResponsiveContainer width="100%" height={180}>
                  <LineChart
                    data={(customerData.daily || []).map(item => ({
                      date: moment(item.date).format('MM/DD'),
                      newUsers: item.newUsers ?? 0,
                      activeUsers: item.activeUsers ?? 0
                    }))}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                    <YAxis
                      tick={{ fontSize: 12 }}
                      tickFormatter={(value) => value.toFixed(0)}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend wrapperStyle={{ fontSize: '12px' }} />
                    <Line
                      type="monotone"
                      dataKey="newUsers"
                      name="New users"
                      stroke="#198754"
                      strokeWidth={2}
                      dot={false}
                    />
                    <Line
                      type="monotone"
                      dataKey="activeUsers"
                      name="Active users"
                      stroke="#0d6efd"
                      strokeWidth={2}
                      dot={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </Card.Body>
            </Card>
          </CardWithLoading>
        </Col>

        <Col xs={12} md={4}>
          <CardWithLoading>
            <Card className="shadow-sm h-100">
              <Card.Body className="p-2 p-md-3">
                <div className="d-flex align-items-center mb-3">
                  <div className="rounded-circle p-2 p-md-3 bg-warning bg-opacity-10">
                    <FaChartLine className="text-warning" size={20} />
                  </div>
                  <div className="ms-2 ms-md-3">
                    <h6 className="mb-0">Unique Page Views</h6>
                    <h3 className="mb-0 fs-4 fs-md-3">
                      {visitData.total}
                      <GrowthBadge value={visitData.growth} />
                    </h3>
                  </div>
                </div>
                <ResponsiveContainer width="100%" height={180}>
                  <LineChart data={getChartData(visitData.daily)}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                    <YAxis 
                      tick={{ fontSize: 12 }} 
                      tickFormatter={(value) => value.toFixed(0)}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend wrapperStyle={{ fontSize: '12px' }} />
                    <Line
                      type="monotone"
                      dataKey="count"
                      name="Unique Page Views"
                      stroke="#ffc107"
                      strokeWidth={2}
                      dot={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
                {visitData.totalPageViewsCount != null && (
                  <div className="mt-2 pt-2 border-top text-center">
                    <span className="text-muted small">Raw count: </span>
                    <span className="fw-semibold">{visitData.totalPageViewsCount}</span>
                  </div>
                )}
              </Card.Body>
            </Card>
          </CardWithLoading>
        </Col>
      </Row>

      <Row className="mt-3 g-3">
        <Col xs={12} lg={8}>
          <CardWithLoading>
            <Card className="shadow-sm">
              <Card.Body className="p-2 p-md-3">
                <h5 className="mb-4 fs-6 fs-md-5">Revenue Overview</h5>
                <div className="d-flex flex-column flex-md-row justify-content-between mb-4">
                  <div className="mb-2 mb-md-0">
                    <h3 className="mb-0 fs-4 fs-md-3">${orderData.revenue.toFixed(2)}</h3>
                    <small className="text-muted">EventGo Revenue</small>
                    <GrowthBadge value={orderData.revenueGrowth} />
                  </div>
                  <div className="text-start text-md-end">
                    <h3 className="mb-0 fs-4 fs-md-3">
                      ${orderData.totalTransactions.toFixed(2)}
                      <GrowthBadge value={orderData.transactionGrowth} />
                    </h3>
                    <small className="text-muted">Total Transactions</small>
                  </div>
                </div>
                <div className="d-flex justify-content-between mb-3 flex-wrap">
                  <div className="mb-2">
                    <h6 className="mb-0 fs-6">Average Order Value</h6>
                    <span className="text-muted small">
                      ${(orderData.totalTransactions / orderData.total || 0).toFixed(2)}
                    </span>
                  </div>
                  <div className="mb-2">
                    <h6 className="mb-0 fs-6">Platform Fee Rate</h6>
                    <span className="text-muted small">
                      {orderData.totalTransactions > 0 ? ((orderData.revenue / orderData.totalTransactions) * 100).toFixed(1) : 0}%
                    </span>
                  </div>
                  <div className="mb-2">
                    <h6 className="mb-0 fs-6">Orders Without Rates</h6>
                    <span className={orderData.ordersWithoutRates > 0 ? "text-warning small fw-bold" : "text-success small"}>
                      {orderData.ordersWithoutRates || 0}
                      {orderData.total > 0 && (
                        <span className="text-muted">
                          {' '}({((orderData.ordersWithoutRates / orderData.total) * 100).toFixed(1)}%)
                        </span>
                      )}
                    </span>
                  </div>
                </div>
                {orderData.ordersWithoutRates > 0 && (
                  <Alert variant="warning" className="mb-0">
                    <small>
                      <strong>{orderData.ordersWithoutRates}</strong> paid order{orderData.ordersWithoutRates !== 1 ? 's' : ''} {orderData.ordersWithoutRates !== 1 ? 'are' : 'is'} missing <code>fees.rates</code> and {orderData.ordersWithoutRates !== 1 ? 'are' : 'is'} using the default 3.5% fee rate. (Free orders are not counted.)
                    </small>
                  </Alert>
                )}
                <ResponsiveContainer width="100%" height={chartHeight}>
                  <LineChart data={orderData.daily.map(item => ({
                    date: moment(item.date).format('MM/DD'),
                    revenue: item.revenue,
                    transactions: item.transactions
                  }))}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                    <YAxis 
                      tick={{ fontSize: 12 }} 
                      tickFormatter={(value) => `$${value.toFixed(2)}`}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend wrapperStyle={{ fontSize: '12px' }} />
                    <Line
                      type="monotone"
                      dataKey="revenue"
                      name="EventGo Revenue"
                      stroke="#0d6efd"
                      strokeWidth={2}
                      dot={false}
                    />
                    <Line
                      type="monotone"
                      dataKey="transactions"
                      name="Transaction Volume"
                      stroke="#20c997"
                      strokeWidth={2}
                      dot={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </Card.Body>
            </Card>
          </CardWithLoading>
        </Col>

        <Col xs={12} lg={4}>
          <CardWithLoading>
            <Card className="shadow-sm h-100">
              <Card.Body className="p-2 p-md-3">
                <h5 className="mb-4 fs-6 fs-md-5">Order Distribution</h5>
                <ResponsiveContainer width="100%" height={chartHeight}>
                  <BarChart data={orderData.daily.map(item => ({
                    date: moment(item.date).format('MM/DD'),
                    paidOrders: orderData.dailyPaidOrders.find(p => moment(p.date).format('MM/DD') === moment(item.date).format('MM/DD'))?.count || 0,
                    freeOrders: orderData.dailyFreeOrders.find(f => moment(f.date).format('MM/DD') === moment(item.date).format('MM/DD'))?.count || 0
                  }))}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                    <YAxis 
                      tick={{ fontSize: 12 }} 
                      tickFormatter={(value) => value.toFixed(0)}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend wrapperStyle={{ fontSize: '12px' }} />
                    <Bar
                      dataKey="paidOrders"
                      name="Paid Orders"
                      stackId="a"
                      fill="rgba(75, 192, 192, 0.8)"
                    />
                    <Bar
                      dataKey="freeOrders"
                      name="Free Orders"
                      stackId="a"
                      fill="rgba(255, 159, 64, 0.8)"
                    />
                  </BarChart>
                </ResponsiveContainer>
                <div className="mt-3">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <span className="small">Paid Orders Rate:</span>
                    <strong>
                      {((orderData.paidOrders / (orderData.paidOrders + orderData.freeOrders)) * 100).toFixed(1)}%
                    </strong>
                  </div>
                  <div className="d-flex justify-content-between align-items-center">
                    <span className="small">Free Orders Rate:</span>
                    <strong>
                      {((orderData.freeOrders / (orderData.paidOrders + orderData.freeOrders)) * 100).toFixed(1)}%
                    </strong>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </CardWithLoading>
        </Col>
      </Row>

      {/* Events ending in this period (events collection, status = live, endDate within range) */}
      <Row className="mt-3 g-3">
        <Col xs={12}>
          <Card className="shadow-sm">
            <Card.Body className="p-2 p-md-3">
              <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                <div className="d-flex flex-column align-items-start gap-1">
                  <h5 className="mb-0 fs-6 fs-md-5">Events Ending This Period</h5>
                  <div className="d-flex flex-wrap gap-2 align-items-center">
                    <Badge bg="success" className="fs-7">
                      {eventsEndingThisPeriod.length} ended this period
                    </Badge>
                    {(() => {
                      let freeCount = 0;
                      let paidCount = 0;
                      eventsEndingThisPeriod.forEach((event) => {
                        const fp = getEventFreePaid(event);
                        if (fp === true) freeCount++;
                        else if (fp === false) paidCount++;
                      });
                      return (
                        <>
                          <Badge bg="success" className="fs-7">
                            Free: {freeCount}
                          </Badge>
                          <Badge bg="primary" className="fs-7">
                            Paid: {paidCount}
                          </Badge>
                        </>
                      );
                    })()}
                    {orgsBehindEndingEvents.length > 0 && (
                      <>
                        <Badge bg="info" className="fs-7 text-dark">
                          {orgsBehindEndingEvents.length} orgs behind these events
                        </Badge>
                        <Badge bg="secondary" className="fs-7">
                          {
                            orgsBehindEndingEvents.filter((o) => o.isNewThisPeriod)
                              .length
                          }{" "}
                          also new orgs this period
                        </Badge>
                      </>
                    )}
                    {totalOrganizationsThisPeriod > 0 && (
                      <>
                        <Badge bg="dark" className="fs-7">
                          Orgs this period: {totalOrganizationsThisPeriod}
                        </Badge>
                        <Badge bg="success" className="fs-7">
                          New orgs: {newOrganizationsCountThisPeriod}
                        </Badge>
                        <Badge bg="primary" className="fs-7">
                          Active orgs (with events): {activeOrganizationsThisPeriod}
                        </Badge>
                      </>
                    )}
                    {eventOrgIdsNotInOrganizationsThisPeriod.length > 0 && (
                      <Badge bg="danger" className="fs-7">
                        Event orgIds not in orgs this period:{" "}
                        {eventOrgIdsNotInOrganizationsThisPeriod.length}
                      </Badge>
                    )}
                  </div>
                </div>
                <div className="d-flex align-items-center gap-2 flex-wrap">
                  <Button
                    variant="outline-primary"
                    size="sm"
                    disabled={eventsPaymentsLoading || !currentUser}
                    onClick={async () => {
                      if (!currentUser?.uid) return;
                      setEventsPaymentsLoading(true);
                      setEventsPaymentsTotal(null);
                      setEventsPaymentsPerEvent({});
                      const totalEvents = eventsEndingThisPeriod.filter(ev => ev.id).length;
                      setEventsPaymentsProgress({ current: 0, total: totalEvents });
                      try {
                        let total = 0;
                        let processed = 0;
                        for (const event of eventsEndingThisPeriod) {
                          if (!event.id) continue;
                          try {
                            const params = new URLSearchParams({
                              auth_id: currentUser.uid,
                              event_id: event.id,
                            });
                            const res = await fetch(`${EVENT_ANALYTICS_API_BASE}/?${params.toString()}`);
                            const data = await res.json();
                            if (res.ok && data?.success && data?.displayData?.totalOrderAmount != null) {
                              const amt = Number(data.displayData.totalOrderAmount) || 0;
                              total += amt;
                              setEventsPaymentsPerEvent(prev => ({
                                ...prev,
                                [event.id]: amt,
                              }));
                            }
                          } catch (e) {
                            // ignore individual event errors
                          } finally {
                            processed += 1;
                            setEventsPaymentsProgress({ current: processed, total: totalEvents });
                          }
                        }
                        setEventsPaymentsTotal(total);
                      } catch (e) {
                        console.error("Error loading payments for events:", e);
                      } finally {
                        setEventsPaymentsLoading(false);
                      }
                    }}
                  >
                    {eventsPaymentsLoading ? (
                      <>
                        <Spinner
                          as="span"
                          animation="border"
                          size="sm"
                          className="me-1"
                        />
                        Loading payments...
                        {eventsPaymentsProgress.total > 0 && (
                          <span className="ms-1">
                            {eventsPaymentsProgress.current}/
                            {eventsPaymentsProgress.total}
                          </span>
                        )}
                      </>
                    ) : (
                      "Load all payments"
                    )}
                  </Button>
                  {eventsPaymentsTotal != null && !eventsPaymentsLoading && (
                    <span className="text-success small ms-1">
                      Total order amount: $
                      {Number(eventsPaymentsTotal).toFixed(0)}
                    </span>
                  )}
                </div>
              </div>
              {totalOrganizationsThisPeriod > 0 && (
                <div className="mt-2">
                  <small className="text-muted d-block mb-1">
                    Organizations this period (from <code>groups</code>):
                  </small>
                  <div className="table-responsive">
                    <Table hover size="sm" className="mb-0">
                      <thead>
                        <tr>
                          <th>Name</th>
                          <th>Org ID</th>
                          <th className="text-end">Members</th>
                          <th className="text-end">Events</th>
                          <th>Created</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {organizationsThisPeriodList.map((org) => {
                          const eventCount =
                            typeof org.eventCount === "number"
                              ? org.eventCount
                              : typeof org.event_count === "number"
                              ? org.event_count
                              : 0;
                          const memberCount =
                            typeof org.memberCount === "number"
                              ? org.memberCount
                              : typeof org.member_count === "number"
                              ? org.member_count
                              : 0;
                          const createdTs =
                            org.timestamp?.toDate?.() ??
                            (org.timestamp instanceof Date ? org.timestamp : null);
                          const createdStr = createdTs
                            ? createdTs.toLocaleDateString()
                            : "—";
                          const isActive = eventCount > 0;
                          return (
                            <tr key={org.id || createdStr + Math.random()}>
                              <td>{org.name || org.groupName || "Untitled Organization"}</td>
                              <td>
                                {org.id ? (
                                  <span className="font-monospace small">{org.id}</span>
                                ) : (
                                  "—"
                                )}
                              </td>
                              <td className="text-end">{memberCount}</td>
                              <td className="text-end">{eventCount}</td>
                              <td>{createdStr}</td>
                              <td>
                                {isActive ? (
                                  <Badge bg="primary" className="fs-7">
                                    Active
                                  </Badge>
                                ) : (
                                  <Badge bg="secondary" className="fs-7">
                                    Inactive
                                  </Badge>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </Table>
                  </div>
                </div>
              )}
              <div className="table-responsive">
                <Table hover size="sm" className="mb-0">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Event</th>
                      <th>Org ID</th>
                      <th>Start Date</th>
                      <th>End Date</th>
                      <th className="text-center">Free/Paid</th>
                      <th className="text-center">Orders</th>
                      <th>Status</th>
                      <th className="text-end">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {eventsEndingThisPeriod.length > 0 ? (
                      eventsEndingThisPeriod.map((event, index) => {
                        const startD = event.startDate?.toDate?.() ?? event.startDate;
                        const endD = event.endDate?.toDate?.() ?? event.endDate;
                        const startDateObj = startD ? (startD instanceof Date ? startD : new Date(startD)) : null;
                        const endDateObj = endD ? (endD instanceof Date ? endD : new Date(endD)) : null;
                        const startStr = startDateObj ? moment(startDateObj).format('MMM D, YYYY h:mm A') : '—';
                        const endStr = endDateObj ? moment(endDateObj).format('MMM D, YYYY h:mm A') : '—';
                        const isPastEvent = endDateObj ? endDateObj < new Date() : (startDateObj ? startDateObj < new Date() : false);
                        const eventTitle = event.title || event.eventDetails?.title || 'Untitled Event';
                        const freePaid = getEventFreePaid(event);
                        const orderInfo = orderDetailsByEventId.get(event.id);
                        const hasOrders = orderInfo && orderInfo.orderCount > 0;
                        const apiAmount = eventsPaymentsPerEvent[event.id];
                        const orderTotal = apiAmount != null ? apiAmount : (orderInfo?.amount ?? 0);
                        const hasData = hasOrders || apiAmount != null;
                        const isLoadingThisEvent = eventsPaymentsLoading && !(event.id in eventsPaymentsPerEvent);
                        return (
                          <tr key={event.id}>
                            <td className="text-muted">{index + 1}</td>
                            <td>
                              <button
                                type="button"
                                className="btn btn-link p-0 fw-semibold text-decoration-none text-start"
                                onClick={() => setEventModalEvent({ id: event.id, title: eventTitle })}
                              >
                                {eventTitle}
                              </button>
                              {event.id && (
                                <div className="small text-muted font-monospace">{event.id}</div>
                              )}
                            </td>
                            <td>
                              {event.orgId ? (
                                <span className="font-monospace small">{event.orgId}</span>
                              ) : (
                                <span className="text-muted small">—</span>
                              )}
                            </td>
                            <td>{startStr}</td>
                            <td>{endStr}</td>
                            <td className="text-center">
                              {freePaid === true ? (
                                <Badge bg="success" className="fs-7">Free</Badge>
                              ) : freePaid === false ? (
                                <Badge bg="primary" className="fs-7">Paid</Badge>
                              ) : (
                                <span className="text-muted">—</span>
                              )}
                            </td>
                            <td className="text-center">
                              {isLoadingThisEvent ? (
                                <span className="text-muted small">Loading…</span>
                              ) : hasData ? (
                                <>
                                  {orderInfo?.orderCount > 0 && (
                                    <Badge bg="primary" className="fs-7 me-1">{orderInfo.orderCount}</Badge>
                                  )}
                                  <span className="text-success small">${Number(orderTotal).toFixed(0)}</span>
                                </>
                              ) : (
                                <span className="text-muted">0</span>
                              )}
                            </td>
                            <td>
                              <Badge bg="success" className="me-1">live</Badge>
                              {isPastEvent && <Badge bg="secondary">Past</Badge>}
                            </td>
                            <td className="text-end">
                              <Button
                                size="sm"
                                variant="outline-primary"
                                onClick={() => navigate(`/event/${event.id}`)}
                              >
                                View
                              </Button>
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan="8" className="text-center text-muted py-4">
                          {dataLoading ? 'Loading...' : 'No events ended in this period'}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Live (not yet ended) and future events in this period */}
      <Row className="mt-3 g-3">
        <Col xs={12}>
          <Card className="shadow-sm">
            <Card.Body className="p-2 p-md-3">
              <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                <h5 className="mb-0 fs-6 fs-md-5">Live & Upcoming Events This Period</h5>
                <div className="d-flex align-items-center gap-2 flex-wrap">
                  <Badge bg="info" className="fs-7">
                    {liveAndFutureEventsThisPeriod.length} live or upcoming
                  </Badge>
                  {(() => {
                    let freeCount = 0;
                    let paidCount = 0;
                    liveAndFutureEventsThisPeriod.forEach(event => {
                      const fp = getEventFreePaid(event);
                      if (fp === true) freeCount++;
                      else if (fp === false) paidCount++;
                    });
                    return (
                      <>
                        <Badge bg="success" className="fs-7">Free: {freeCount}</Badge>
                        <Badge bg="primary" className="fs-7">Paid: {paidCount}</Badge>
                      </>
                    );
                  })()}
                </div>
              </div>
              <div className="table-responsive">
                <Table hover size="sm" className="mb-0">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Event</th>
                      <th>Start Date</th>
                      <th>End Date</th>
                      <th className="text-center">Free/Paid</th>
                      <th>Status</th>
                      <th className="text-end">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {liveAndFutureEventsThisPeriod.length > 0 ? (
                      liveAndFutureEventsThisPeriod.map((event, index) => {
                        const startD = event.startDate?.toDate?.() ?? event.startDate;
                        const endD = event.endDate?.toDate?.() ?? event.endDate;
                        const startDateObj = startD ? (startD instanceof Date ? startD : new Date(startD)) : null;
                        const endDateObj = endD ? (endD instanceof Date ? endD : new Date(endD)) : null;
                        const startStr = startDateObj ? moment(startDateObj).format('MMM D, YYYY h:mm A') : '—';
                        const endStr = endDateObj ? moment(endDateObj).format('MMM D, YYYY h:mm A') : '—';
                        const eventTitle = event.title || event.eventDetails?.title || 'Untitled Event';
                        const freePaid = getEventFreePaid(event);
                        const now = new Date();
                        const isUpcoming = startDateObj && startDateObj > now;
                        return (
                          <tr key={event.id}>
                            <td className="text-muted">{index + 1}</td>
                            <td>
                              <button
                                type="button"
                                className="btn btn-link p-0 fw-semibold text-decoration-none text-start"
                                onClick={() => setEventModalEvent({ id: event.id, title: eventTitle })}
                              >
                                {eventTitle}
                              </button>
                              {event.id && (
                                <div className="small text-muted font-monospace">{event.id}</div>
                              )}
                            </td>
                            <td>{startStr}</td>
                            <td>{endStr}</td>
                            <td className="text-center">
                              {freePaid === true ? (
                                <Badge bg="success" className="fs-7">Free</Badge>
                              ) : freePaid === false ? (
                                <Badge bg="primary" className="fs-7">Paid</Badge>
                              ) : (
                                <span className="text-muted">—</span>
                              )}
                            </td>
                            <td>
                              {isUpcoming ? (
                                <Badge bg="warning" text="dark">upcoming</Badge>
                              ) : (
                                <Badge bg="success">live</Badge>
                              )}
                            </td>
                            <td className="text-end">
                              <Button
                                size="sm"
                                variant="outline-primary"
                                onClick={() => navigate(`/event/${event.id}`)}
                              >
                                View
                              </Button>
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan="7" className="text-center text-muted py-4">
                          {dataLoading ? 'Loading...' : 'No live or upcoming events in this period'}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Order Details by event (from summary orders > orderDetails: amount, eventId, userEmail, userName, userUID) */}
      <Row className="mt-3 g-3">
        <Col xs={12}>
          <CardWithLoading>
            <Card className="shadow-sm">
              <Card.Body className="p-2 p-md-3">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h5 className="mb-0 fs-6 fs-md-5">Order Details by Event</h5>
                  <Badge bg="primary" className="fs-7">
                    {orderDetailsByEvent.length} event{orderDetailsByEvent.length !== 1 ? 's' : ''} · {orderDetailsList.length} order{orderDetailsList.length !== 1 ? 's' : ''}
                  </Badge>
                </div>
                <div className="table-responsive">
                  <Table hover size="sm" className="mb-0">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Event</th>
                        <th className="text-center">Order Count</th>
                        <th className="text-end">Amount</th>
                        <th>Last Order Date</th>
                        <th>Last Purchaser</th>
                      </tr>
                    </thead>
                    <tbody>
                      {orderDetailsByEvent.length > 0 ? (
                        orderDetailsByEvent.map((row, index) => {
                          const eventName = eventIdToTitle.get(row.eventId) || null;
                          return (
                          <tr key={row.eventId}>
                            <td className="text-muted">{index + 1}</td>
                            <td>
                              <div className="fw-semibold">{eventName || row.eventId}</div>
                              {eventName && (
                                <code className="small text-muted">{row.eventId}</code>
                              )}
                            </td>
                            <td className="text-center">
                              <Badge bg="success">{row.orderCount}</Badge>
                            </td>
                            <td className="text-end">
                              <span className={row.amount > 0 ? 'text-success fw-semibold' : 'text-muted'}>
                                ${(row.amount || 0).toFixed(2)}
                              </span>
                            </td>
                            <td>{row.lastOrderDate ? moment(row.lastOrderDate).format('MMM D, YYYY') : '—'}</td>
                            <td>{row.lastPurchaser || '—'}</td>
                          </tr>
                          );
                        })
                      ) : (
                        <tr>
                          <td colSpan="6" className="text-center text-muted py-4">
                            {dataLoading ? 'Loading...' : 'No order details in this period'}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </div>
              </Card.Body>
            </Card>
          </CardWithLoading>
        </Col>
      </Row>

      {/* Event Views by Day (from summary.eventViews or summary.pageViews.eventViews), top 20 per day - days as tabs */}
      <Row className="mt-3 g-3">
        <Col xs={12}>
          <Card className="shadow-sm">
            <Card.Body className="p-2 p-md-3">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="mb-0 fs-6 fs-md-5">Event Views by Day</h5>
                <Badge bg="info" className="fs-7">
                  Top 20 per day
                </Badge>
              </div>
              {eventViewsByDay.length === 0 ? (
                <p className="text-muted small mb-0 py-3 text-center">
                  {dataLoading ? 'Loading...' : 'No event view data in this period'}
                </p>
              ) : (
                <Tab.Container defaultActiveKey="overall">
                  <Nav variant="tabs" className="flex-wrap mb-3">
                    <Nav.Item>
                      <Nav.Link eventKey="overall">Overall</Nav.Link>
                    </Nav.Item>
                    {eventViewsByDay.map(dayRow => (
                      <Nav.Item key={dayRow.date}>
                        <Nav.Link eventKey={dayRow.date}>
                          {moment(dayRow.date).format('MMM D')}
                        </Nav.Link>
                      </Nav.Item>
                    ))}
                  </Nav>
                  <Tab.Content>
                    <Tab.Pane eventKey="overall">
                      <h6 className="mb-2 mt-0">Top 20 by views</h6>
                      <div className="table-responsive mb-4">
                        <Table hover size="sm" className="mb-0">
                          <thead>
                            <tr>
                              <th>#</th>
                              <th>Event</th>
                              <th className="text-end">Total Views</th>
                            </tr>
                          </thead>
                          <tbody>
                            {eventViewsOverallTop20.length === 0 ? (
                              <tr>
                                <td colSpan="3" className="text-center text-muted py-2 small">No event views in this period</td>
                              </tr>
                            ) : (
                              eventViewsOverallTop20.map((ev, idx) => (
                                <tr key={ev.eventId}>
                                  <td className="text-muted">{idx + 1}</td>
                                  <td>
                                    <span className="fw-semibold">{eventIdToTitle.get(ev.eventId) || ev.eventId}</span>
                                    {eventIdToTitle.get(ev.eventId) && (
                                      <code className="small text-muted ms-1">{ev.eventId}</code>
                                    )}
                                  </td>
                                  <td className="text-end">
                                    <Badge bg="info">{ev.count}</Badge>
                                  </td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </Table>
                      </div>
                      <h6 className="mb-2">Events to watch (growing popularity)</h6>
                      <p className="small text-muted mb-2">Events with more views in the second half of the period than the first half.</p>
                      <div className="table-responsive">
                        <Table hover size="sm" className="mb-0">
                          <thead>
                            <tr>
                              <th>#</th>
                              <th>Event</th>
                              <th className="text-end">First half</th>
                              <th className="text-end">Second half</th>
                              <th className="text-end">Growth</th>
                            </tr>
                          </thead>
                          <tbody>
                            {eventViewsGrowingPopularity.length === 0 ? (
                              <tr>
                                <td colSpan="5" className="text-center text-muted py-2 small">No growing events in this period</td>
                              </tr>
                            ) : (
                              eventViewsGrowingPopularity.map((ev, idx) => (
                                <tr key={ev.eventId}>
                                  <td className="text-muted">{idx + 1}</td>
                                  <td>
                                    <span className="fw-semibold">{eventIdToTitle.get(ev.eventId) || ev.eventId}</span>
                                    {eventIdToTitle.get(ev.eventId) && (
                                      <code className="small text-muted ms-1">{ev.eventId}</code>
                                    )}
                                  </td>
                                  <td className="text-end">{ev.first}</td>
                                  <td className="text-end">{ev.second}</td>
                                  <td className="text-end">
                                    <Badge bg="success">+{ev.growth}</Badge>
                                  </td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </Table>
                      </div>
                    </Tab.Pane>
                    {eventViewsByDay.map(dayRow => (
                      <Tab.Pane key={dayRow.date} eventKey={dayRow.date}>
                        <div className="table-responsive">
                          <Table hover size="sm" className="mb-0">
                            <thead>
                              <tr>
                                <th>#</th>
                                <th>Event</th>
                                <th className="text-end">Views</th>
                              </tr>
                            </thead>
                            <tbody>
                              {dayRow.topEvents.length === 0 ? (
                                <tr>
                                  <td colSpan="3" className="text-center text-muted py-2 small">No event views</td>
                                </tr>
                              ) : (
                                dayRow.topEvents.map((ev, idx) => (
                                  <tr key={`${dayRow.date}-${ev.eventId}`}>
                                    <td className="text-muted">{idx + 1}</td>
                                    <td>
                                      <span className="fw-semibold">{eventIdToTitle.get(ev.eventId) || ev.eventId}</span>
                                      {eventIdToTitle.get(ev.eventId) && (
                                        <code className="small text-muted ms-1">{ev.eventId}</code>
                                      )}
                                    </td>
                                    <td className="text-end">
                                      <Badge bg="info">{ev.count}</Badge>
                                    </td>
                                  </tr>
                                ))
                              )}
                            </tbody>
                          </Table>
                        </div>
                      </Tab.Pane>
                    ))}
                  </Tab.Content>
                </Tab.Container>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <DateRangePickerModal />
      <MonthPickerModal />
      <LoadDataModal />

      <Modal
        show={!!eventModalEvent}
        onHide={() => setEventModalEvent(null)}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>{eventModalData?.eventName ?? eventModalEvent?.title ?? 'Event summary'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {eventModalLoading ? (
            <div className="text-center py-4">
              <Spinner animation="border" size="sm" /> Loading event analytics...
            </div>
          ) : eventModalData?.error ? (
            <Alert variant="danger" className="mb-0">{eventModalData.error}</Alert>
          ) : eventModalData ? (
            <>
              <Row className="g-2 mb-3">
                <Col xs={6} md={4}>
                  <Card className="border-success h-100">
                    <Card.Body className="py-2 px-3 text-center">
                      <Card.Text className="small text-muted mb-0">Total orders</Card.Text>
                      <Card.Title className="mb-0 fs-6">{eventModalData.totalOrders ?? 0}</Card.Title>
                    </Card.Body>
                  </Card>
                </Col>
                <Col xs={6} md={4}>
                  <Card className="border-info h-100">
                    <Card.Body className="py-2 px-3 text-center">
                      <Card.Text className="small text-muted mb-0">Total order amount</Card.Text>
                      <Card.Title className="mb-0 fs-6">
                        ${Number(eventModalData.totalOrderAmount ?? 0).toFixed(2)}
                      </Card.Title>
                    </Card.Body>
                  </Card>
                </Col>
                <Col xs={6} md={4}>
                  <Card className="border-warning h-100">
                    <Card.Body className="py-2 px-3 text-center">
                      <Card.Text className="small text-muted mb-0">Total views</Card.Text>
                      <Card.Title className="mb-0 fs-6">{eventModalData.totalViews ?? 0}</Card.Title>
                    </Card.Body>
                  </Card>
                </Col>
                <Col xs={6} md={4}>
                  <Card className="border-primary h-100">
                    <Card.Body className="py-2 px-3 text-center">
                      <Card.Text className="small text-muted mb-0">Total real views</Card.Text>
                      <Card.Title className="mb-0 fs-6">{eventModalData.totalRealViews ?? 0}</Card.Title>
                    </Card.Body>
                  </Card>
                </Col>
                <Col xs={6} md={4}>
                  <Card className="border-secondary h-100">
                    <Card.Body className="py-2 px-3 text-center">
                      <Card.Text className="small text-muted mb-0">Order conversion rate (%)</Card.Text>
                      <Card.Title className="mb-0 fs-6">{eventModalData.totalOrderConversionRates ?? 0}</Card.Title>
                    </Card.Body>
                  </Card>
                </Col>
                <Col xs={6} md={4}>
                  <Card className="border-dark h-100">
                    <Card.Body className="py-2 px-3 text-center">
                      <Card.Text className="small text-muted mb-0">Spending rate</Card.Text>
                      <Card.Title className="mb-0 fs-6">{eventModalData.totalSpendingRates ?? 0}</Card.Title>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </>
          ) : (
            <p className="text-muted mb-0">No data.</p>
          )}
        </Modal.Body>
      </Modal>
    </Container>
  );
} 